/**
 * AdminPage Component
 *
 * Admin dashboard for tenant management, user administration, and settings.
 * Shows hierarchy tree for all admin roles.
 */

import { useState, useEffect, useRef, useCallback, forwardRef } from 'react';
import { activeLocale } from '@/utils/locale';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTenantPath } from '@/routing';
import { Table, Input, Button, Space, Radio, Checkbox, Dropdown, DatePicker, Select, Popover, Tag, message, Modal } from 'antd';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { SearchOutlined, SettingOutlined, EditOutlined, DeleteOutlined, ShareAltOutlined, TeamOutlined, ApartmentOutlined, MailOutlined, KeyOutlined, SendOutlined } from '@ant-design/icons';
import type { FilterDropdownProps } from 'antd/es/table/interface';
import { Sidebar } from '@/components/layout/Sidebar';
import { ConfirmModal } from '@/components/common';
import { getInitials } from '@/utils/getInitials';
import { StatsCard } from '@modules/admin/components/StatsCard';
import TenantsTableCard from '@modules/admin/components/TenantsTableCard';
import RenameTenantModal from '@modules/admin/components/RenameTenantModal';
import SystemDesignTab from '@modules/admin/components/SystemDesignTab';
import ConnectorLibraryTab from '@modules/connector_library/components/ConnectorLibraryTab';
import { authService, useAuth } from '@modules/auth';
import { useFeature } from '@/hooks/useFeature';
import { useLanguage } from '@/hooks/useLanguage';
import { mirrorPlacement } from '@/utils/rtl';
import adminService from '@modules/admin/services/adminService';
import { groupAdminService } from '@modules/group_admin/services/groupAdminService';
import { validatePasswordPolicy } from '@/utils/passwordPolicy';
import botService from '@modules/chatbots/services/botService';
import type { TenantHierarchy, HierarchyTreeNode, TenantUser, BotAnalytics } from '@modules/group_admin/types';
import TenantContextEditor from '@modules/group_admin/components/TenantContextEditor';
import './AdminPage.css';
import '@modules/conversations/components/MessageItem.css';
import '@modules/conversations/components/ChatInput.css';
import '@modules/chatbots/components/BotShareModal.css';
import { emitJourneyEvent, JOURNEY_EVENTS } from '@modules/journey/detect';

interface DashboardStats {
  total_users: number;
  active_users: number;
  total_conversations: number;
  total_messages: number;
}

// Tree visualisation lives in components/admin/ so the SuperAdminPage's
// Tenant Tree tab can reuse the exact same widget without duplication.
import {
  HierarchyTreeNode as HierarchyTreeNodeComponent,
  levelStyles,
  getLevelIcon,
} from '@/components/admin/HierarchyTreeNode';

// Local alias keeps the rest of this file unchanged — the imported
// component exposes the same JSX/props as the previous in-file definition.
const HierarchyTreeNode = HierarchyTreeNodeComponent;

/**
 * Pull a human-readable reason out of an API failure.
 *
 * The backend answers with `{ error }`, and `@validate_request` adds a
 * `validation_errors` array. Anything we can't interpret falls back to the
 * caller's plain-language default rather than leaking a stack trace or a bare
 * "Request failed with status code 400" at the user.
 */
function extractApiError(error: unknown, fallback: string): string {
  if (!error || typeof error !== 'object') return fallback;

  const err = error as {
    response?: { data?: { error?: string; validation_errors?: unknown[] } };
    code?: string;
  };

  const data = err.response?.data;
  if (data?.error) {
    const details = Array.isArray(data.validation_errors)
      ? data.validation_errors.filter((d): d is string => typeof d === 'string')
      : [];
    return details.length ? `${data.error} (${details.join('; ')})` : data.error;
  }

  // No response at all → the request never reached the server.
  if (!err.response) return fallback;

  return fallback;
}

/**
 * Error banner shown inside a modal, next to the form that produced it.
 * Forwards a ref so the parent can scroll it into view when it appears.
 */
const ModalErrorBanner = forwardRef<HTMLDivElement, { message: string }>(function ModalErrorBanner({ message: text }, ref) {
  if (!text) return null;
  return (
    <div
      ref={ref}
      role="alert"
      style={{
        background: 'var(--claude-bg-tertiary)',
        border: '1px solid var(--claude-error, #D50032)',
        borderRadius: 'var(--radius)',
        padding: '12px 16px',
        marginBottom: '16px',
        display: 'flex',
        alignItems: 'flex-start',
        gap: '10px',
      }}
    >
      <svg
        viewBox="0 0 24 24"
        width="18"
        height="18"
        style={{
          fill: 'none',
          stroke: 'var(--claude-error, #D50032)',
          strokeWidth: 2,
          flexShrink: 0,
          marginTop: '1px',
        }}
      >
        <circle cx="12" cy="12" r="10" />
        <line x1="12" y1="8" x2="12" y2="13" />
        <line x1="12" y1="17" x2="12.01" y2="17" />
      </svg>
      <div style={{ fontSize: 'var(--text-sm)', color: 'var(--claude-text-primary)' }}>{text}</div>
    </div>
  );
});

// Role priority for sorting (higher = more important, shown first)
const ROLE_PRIORITY: Record<string, number> = {
  'super_admin': 4,
  'group_admin': 3,
  'admin': 2,
  'user': 1,
};

// Sort users by role priority (descending) then alphabetically by email
function sortUsersByRole<T extends { role: string; email: string }>(users: T[]): T[] {
  return [...users].sort((a, b) => {
    const rolePriorityA = ROLE_PRIORITY[a.role] ?? 0;
    const rolePriorityB = ROLE_PRIORITY[b.role] ?? 0;
    // Higher priority first
    if (rolePriorityA !== rolePriorityB) {
      return rolePriorityB - rolePriorityA;
    }
    // Then alphabetically by email
    return a.email.toLowerCase().localeCompare(b.email.toLowerCase());
  });
}

interface User {
  user_id: string;
  email: string;
  full_name: string | null;
  role: string;
  is_active: boolean;
  created_at: string;
  last_login_at: string | null;
}

export default function AdminPage() {
  // Journey detection: reaching the admin surfaces at all is the Try for the
  // "what you administer" step. Which tabs exist here already depends on role,
  // so the journey never has to re-derive that.
  useEffect(() => {
    emitJourneyEvent(JOURNEY_EVENTS.adminOpened);
  }, []);
  const navigate = useNavigate();
  const { t, direction } = useLanguage();
  const tenantPath = useTenantPath();
  const { isAdmin, isSuperAdmin, isGroupAdmin, isLoading: authLoading, tenantId: currentUserTenantId, user } = useAuth();
  const tenantContextFeatureEnabled = useFeature('tenant_context.editor');
  // The connector library: group admins and super admins, while either kind
  // of connector is switched on for the organization.
  const mcpConnectorsOn = useFeature('integrations.mcp_connectors');
  const apiConnectorsOn = useFeature('integrations.api_connectors');
  // Own-tenant `auth.transactional_email` — used as the fallback for user
  // rows that don't carry a per-row flag (admin-role Users tab, whose rows
  // come from AnalyticsService and are always the admin's own tenant).
  const emailFeatureForOwnTenant = useFeature('auth.transactional_email');
  const currentUserId = user?.user_id;
  // `?tab=connector-library` opens a tab directly (the agent editor's
  // "Manage library" link); every other tab is chosen on the page.
  const [searchParams] = useSearchParams();
  const [currentTab, setCurrentTab] = useState<string>(
    () => (searchParams.get('tab') === 'connector-library' ? 'connector-library' : 'hierarchy'));
  const libraryTabAllowed = (isSuperAdmin || isGroupAdmin) && (mcpConnectorsOn || apiConnectorsOn);
  // A link to the library for someone who cannot see it opens the first tab.
  useEffect(() => {
    if (!authLoading && currentTab === 'connector-library' && !libraryTabAllowed) setCurrentTab('hierarchy');
  }, [authLoading, currentTab, libraryTabAllowed]);
  const [loading, setLoading] = useState(true);

  // Dashboard state
  const [stats, setStats] = useState<DashboardStats>({
    total_users: 0,
    active_users: 0,
    total_conversations: 0,
    total_messages: 0,
  });
  const [recentUsers, setRecentUsers] = useState<User[]>([]);

  // Users tab state - using TenantUser for all users across tenants
  const [allUsers, setAllUsers] = useState<TenantUser[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);

  // Settings tab state
  const [tenantPrompt, setTenantPrompt] = useState<string>('');
  const [settingsLoading, setSettingsLoading] = useState(false);

  // Hierarchy tab state
  const [tenants, setTenants] = useState<TenantHierarchy[]>([]);
  const [hierarchyTree, setHierarchyTree] = useState<HierarchyTreeNode | null>(null);
  const [hierarchyLoading, setHierarchyLoading] = useState(false);
  const [selectedTenantId, setSelectedTenantId] = useState<string | null>(null);
  const [selectedTenantName, setSelectedTenantName] = useState<string | null>(null);
  const [_selectedTenantHasChildren, setSelectedTenantHasChildren] = useState<boolean>(false);
  const [_selectedTenantIsRoot, setSelectedTenantIsRoot] = useState<boolean>(false);
  const [tenantUsers, setTenantUsers] = useState<TenantUser[]>([]);
  const [tenantUsersLoading, setTenantUsersLoading] = useState(false);
  const [collapsedNodes, setCollapsedNodes] = useState<Set<string>>(new Set());

  // Column visibility and width state for Users table
  const [visibleUsersColumns, setVisibleUsersColumns] = useState<string[]>([
    'email',
    'tenant_name',
    'role',
    'user_tier',
    'is_active',
    'total_conversations',
    'total_messages',
    'active_days',
    'created_at',
    'last_login'
  ]);
  const [usersColumnWidths, setUsersColumnWidths] = useState<Record<string, number | undefined>>({
    email: 260,
    tenant_name: 180,
    role: 180,
    user_tier: 110,
    is_active: 170,
    total_conversations: 200,
    total_messages: 180,
    active_days: 180,
    created_at: 180,
    last_login: 180,
    actions: 100
  });

  // The element the Organization users table actually scrolls inside. antd
  // anchors its sticky horizontal scrollbar to the WINDOW unless told
  // otherwise, and here the scroller is an ancestor card — with the wrong
  // container it renders no sticky bar at all. Walking up from the table
  // finds the real one without hardcoding a class name that markup changes
  // could quietly invalidate.
  const orgUsersWrapRef = useRef<HTMLDivElement | null>(null);
  const orgUsersScrollParent = useCallback((): HTMLElement | Window => {
    let el: HTMLElement | null = orgUsersWrapRef.current?.parentElement ?? null;
    while (el && el !== document.body) {
      const oy = getComputedStyle(el).overflowY;
      if ((oy === 'auto' || oy === 'scroll') && el.scrollHeight > el.clientHeight) return el;
      el = el.parentElement;
    }
    return window;
  }, []);

  // Organization users table column widths (hierarchy tab)
  const [orgUsersColumnWidths, setOrgUsersColumnWidths] = useState<Record<string, number | undefined>>({
    email: 250,
    role: 180,
    is_active: 150,
    actions: 100
  });

  // Bots tab state
  const [allBots, setAllBots] = useState<BotAnalytics[]>([]);
  const [botsLoading, setBotsLoading] = useState(false);
  const [shareSearchTerm, setShareSearchTerm] = useState('');
  const [visibleBotsColumns, setVisibleBotsColumns] = useState<string[]>([
    'chatbot_name',
    'creator_email',
    'sharing',
    'total_conversations',
    'total_messages',
    // 'total_cost_usd', // Hidden for now - uncomment to show cost column
    'unique_users',
    'actions'
  ]);
  const [botsColumnWidths, setBotsColumnWidths] = useState<Record<string, number | undefined>>({
    chatbot_name: 220,
    creator_email: 240,
    sharing: 140,
    total_conversations: 200,
    total_messages: 180,
    total_cost_usd: 140,
    unique_users: 190,
    actions: 160
  });

  // Analytics tab state
  const [selectedAnalyticsBot, setSelectedAnalyticsBot] = useState<string | null>(null);
  const [dailyUsageData, setDailyUsageData] = useState<{ date: string; tokens: number; messages: number; conversations: number }[]>([]);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsTimeRange, setAnalyticsTimeRange] = useState<number>(30);
  // Brush selection for zoom (indices into dailyUsageData)
  const [brushStart, setBrushStart] = useState<number | null>(null);
  const [brushEnd, setBrushEnd] = useState<number | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartX, setDragStartX] = useState<number | null>(null);
  const [dragCurrentX, setDragCurrentX] = useState<number | null>(null);

  // Bot Insights tab state
  const [insightsBots, setInsightsBots] = useState<{ chatbot_id: string; chatbot_name: string }[]>([]);
  const [selectedInsightsBot, setSelectedInsightsBot] = useState<string | null>(null);
  const [insightsQuery, setInsightsQuery] = useState('');
  const [insightsMessages, setInsightsMessages] = useState<{ role: 'user' | 'assistant'; content: string }[]>([]);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [insightsSummaries, setInsightsSummaries] = useState<string[]>([]);
  const [insightsSummariesLoading, setInsightsSummariesLoading] = useState(false);

  // Modal state
  const [showCreateUserModal, setShowCreateUserModal] = useState(false);
  const [createUserForm, setCreateUserForm] = useState({
    email: '',
    full_name: '',
    password: '',
    tenant_id: '',
  });
  // Create-user mode: 'password' = admin types one now; 'invite' = user is
  // created pending and receives an email to set their own (group_admin+).
  const [createUserMode, setCreateUserMode] = useState<'password' | 'invite'>('password');
  // Rendered as a banner INSIDE the modal. A toast alone is easy to miss (and
  // historically rendered behind the overlay), and the duplicate-email case
  // needs to stay on screen while the admin edits the address.
  const [createUserError, setCreateUserError] = useState('');
  const [createUserSubmitting, setCreateUserSubmitting] = useState(false);

  // Modal state for creating users in selected tenant (Organization tab)
  const [showTenantUserModal, setShowTenantUserModal] = useState(false);
  const [tenantUserForm, setTenantUserForm] = useState({
    email: '',
    display_name: '',
    password: '',
  });
  const [tenantUserMode, setTenantUserMode] = useState<'password' | 'invite'>('password');
  const [tenantUserError, setTenantUserError] = useState('');
  const [tenantUserSubmitting, setTenantUserSubmitting] = useState(false);

  // Set when the tenant list fails to load, so the Add-User modal can say so
  // instead of just showing an empty dropdown with no explanation.
  const [tenantsLoadError, setTenantsLoadError] = useState('');

  // Refs to the two Add-User error banners. When a banner appears it is
  // smoothly scrolled into view so the message is seen even if the admin had
  // scrolled the form down to the submit button. (The scroll container is the
  // modal's <form>, so scrollIntoView — which walks up to the real scrollable
  // ancestor — is more robust than scrolling a fixed element.)
  const createUserErrorRef = useRef<HTMLDivElement>(null);
  const tenantUserErrorRef = useRef<HTMLDivElement>(null);

  // Set-password modal (admin directly sets a user's password, no email)
  const [setPasswordModal, setSetPasswordModal] = useState({
    isOpen: false, tenantId: '', userId: '', userEmail: '',
  });
  const [setPasswordForm, setSetPasswordForm] = useState({ password: '', confirm: '' });
  const [setPasswordLoading, setSetPasswordLoading] = useState(false);
  const [setPasswordError, setSetPasswordError] = useState('');

  // Invitations are group_admin+ only (mirrors the backend @require_group_admin gate)
  const canInvite = isGroupAdmin || isSuperAdmin;

  // Whether a given user row's tenant allows password-reset / invitation
  // EMAILS. group_admin / super_admin rows carry `transactional_email`
  // per row; admin-role rows lack it and fall back to the admin's own-tenant
  // feature flag. Controls visibility of the "reset email" / "resend invite"
  // row actions (the backend still enforces — this only hides doomed UI).
  const rowEmailEnabled = (u: TenantUser): boolean =>
    u.transactional_email ?? emailFeatureForOwnTenant;

  // "Send invitation email" is only offered when the SELECTED target tenant
  // has transactional email on. No tenant selected → not allowed (you can't
  // invite without a target). Drives the Add-User modals' invite radio.
  const createUserInviteAllowed =
    !!tenants.find(t => t.tenant_id === createUserForm.tenant_id)?.transactional_email;
  const tenantUserInviteAllowed =
    !!tenants.find(t => t.tenant_id === selectedTenantId)?.transactional_email;

  // Force the mode back to "password" whenever the selected tenant can't
  // receive invitation emails (tenant switched to an email-off tenant, or
  // the selection was cleared) so a hidden/disabled invite option can never
  // remain the active choice.
  useEffect(() => {
    if (!createUserInviteAllowed && createUserMode === 'invite') {
      setCreateUserMode('password');
    }
  }, [createUserInviteAllowed, createUserMode]);

  useEffect(() => {
    if (!tenantUserInviteAllowed && tenantUserMode === 'invite') {
      setTenantUserMode('password');
    }
  }, [tenantUserInviteAllowed, tenantUserMode]);

  // Modal state for creating new tenant
  const [showCreateTenantModal, setShowCreateTenantModal] = useState(false);
  // Rename (group admins and super admins): its own guarded two-step dialog.
  const [renameTarget, setRenameTarget] = useState<{ tenant_id: string; tenant_name: string } | null>(null);
  const [createTenantForm, setCreateTenantForm] = useState({
    tenant_name: '',
    tenant_code: '',
    parent_tenant_id: '',
  });

  // Parent tenant dropdown state
  const [parentTenantSearchQuery, setParentTenantSearchQuery] = useState('');
  const [isParentDropdownOpen, setIsParentDropdownOpen] = useState(false);
  const parentDropdownRef = useRef<HTMLDivElement>(null);

  // Tenant picker for the Users-tab "Add User" modal. Uses the same
  // hierarchy-styled searchable-select as the "share bot to tenant" picker
  // (renders inside the modal, so it can't be occluded by the overlay).
  const [userTenantSearchQuery, setUserTenantSearchQuery] = useState('');
  const [isUserTenantDropdownOpen, setIsUserTenantDropdownOpen] = useState(false);
  const userTenantDropdownRef = useRef<HTMLDivElement>(null);

  // Delete confirmation modal state
  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    type: 'user' | 'tenant' | null;
    source: 'users-tab' | 'org-tab' | null;
    id: string;
    name: string;
    loading: boolean;
  }>({
    isOpen: false,
    type: null,
    source: null,
    id: '',
    name: '',
    loading: false,
  });

  // Check admin access (allow admin, group_admin, and super_admin)
  // Wait for auth to finish loading before checking access
  useEffect(() => {
    if (authLoading) return; // Don't check until auth state is loaded

    if (!isAdmin && !isSuperAdmin && !isGroupAdmin) {
      console.log('[ADMIN] Access denied - redirecting to home');
      navigate(tenantPath('/'));
    }
  }, [authLoading, isAdmin, isSuperAdmin, isGroupAdmin, navigate, tenantPath]);

  // Load dashboard data and hierarchy on mount (hierarchy is the default tab).
  // Gated on auth being resolved — firing these before the token/tenant headers
  // exist returned 401s and left `tenants` empty for the rest of the session.
  useEffect(() => {
    if (authLoading) return;
    loadDashboardData();
    loadHierarchy();
  }, [authLoading]);

  // Close parent dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (parentDropdownRef.current && !parentDropdownRef.current.contains(event.target as Node)) {
        setIsParentDropdownOpen(false);
      }
    };

    if (isParentDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isParentDropdownOpen]);

  // Close the Add-User tenant dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userTenantDropdownRef.current && !userTenantDropdownRef.current.contains(event.target as Node)) {
        setIsUserTenantDropdownOpen(false);
      }
    };
    if (isUserTenantDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isUserTenantDropdownOpen]);

  // Build full hierarchy path for a tenant (e.g., "Root / Child1 / Child2")
  const buildTenantHierarchyPath = (tenant: TenantHierarchy): string => {
    const path: string[] = [];
    let currentTenant: TenantHierarchy | undefined = tenant;

    // Walk up the parent chain to build the path
    while (currentTenant) {
      path.unshift(currentTenant.tenant_name);
      if (currentTenant.parent_tenant_id) {
        currentTenant = tenants.find(t => t.tenant_id === currentTenant?.parent_tenant_id);
      } else {
        break;
      }
    }

    return path.join(' / ');
  };

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const [dashboardStats, usersResponse] = await Promise.all([
        adminService.getDashboardStats(),
        adminService.getUsers({ per_page: 5 }),
      ]);

      setStats(dashboardStats);
      setRecentUsers(usersResponse.users);
    } catch (error) {
      console.error('[ERROR] Failed to load dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      setUsersLoading(true);
      // Use groupAdminService to get all users with tenant info
      const users = await groupAdminService.listAllUsers();
      // Sort users by tenant name, then by role priority, then by email
      const rolePriority: Record<string, number> = {
        'super_admin': 0,
        'group_admin': 1,
        'admin': 2,
        'user': 3
      };
      const sortedUsers = [...users].sort((a, b) => {
        // First sort by tenant name
        const tenantCompare = (a.tenant_name || '').localeCompare(b.tenant_name || '');
        if (tenantCompare !== 0) return tenantCompare;
        // Then by role priority
        const roleCompare = (rolePriority[a.role] ?? 4) - (rolePriority[b.role] ?? 4);
        if (roleCompare !== 0) return roleCompare;
        // Finally by email
        return a.email.localeCompare(b.email);
      });
      setAllUsers(sortedUsers);
    } catch (error) {
      console.error('[ERROR] Failed to load users:', error);
    } finally {
      setUsersLoading(false);
    }
  };

  const loadSettings = async () => {
    try {
      setSettingsLoading(true);
      const settings = await adminService.getTenantSettings();
      setTenantPrompt(settings.tenant_prompt || '');
    } catch (error) {
      console.error('[ERROR] Failed to load settings:', error);
    } finally {
      setSettingsLoading(false);
    }
  };

  const loadHierarchy = async () => {
    setHierarchyLoading(true);
    // Settled, not Promise.all: these two calls feed independent UI. Under
    // Promise.all a failing hierarchy tree also threw away a perfectly good
    // tenant list, which is what left the Add-User tenant dropdown empty.
    const [tenantsResult, hierarchyResult] = await Promise.allSettled([
      groupAdminService.listTenants(),
      groupAdminService.getHierarchyTree(),
    ]);

    if (tenantsResult.status === 'fulfilled') {
      setTenants(tenantsResult.value);
      setTenantsLoadError('');
    } else {
      console.error('[ERROR] Failed to load tenants:', tenantsResult.reason);
      setTenantsLoadError(
        t('admin.userManagement.tenantsLoadFailed', {
          defaultValue: "Couldn't load the list of organizations. Check your connection and try again.",
        }),
      );
    }

    if (hierarchyResult.status === 'fulfilled') {
      setHierarchyTree(hierarchyResult.value.hierarchy);
    } else {
      console.error('[ERROR] Failed to load hierarchy tree:', hierarchyResult.reason);
    }

    setHierarchyLoading(false);
  };

  const loadBots = async () => {
    try {
      setBotsLoading(true);
      const bots = await groupAdminService.listBotAnalytics();
      // Filter only active bots and sort by total conversations descending
      const activeBots = bots.filter(b => b.is_active);
      const sortedBots = [...activeBots].sort((a, b) => b.total_conversations - a.total_conversations);
      setAllBots(sortedBots);
    } catch (error) {
      console.error('[ERROR] Failed to load bots:', error);
    } finally {
      setBotsLoading(false);
    }
  };

  const handleDeleteBot = async (botId: string, botName: string) => {
    if (!window.confirm(t('common.confirmDeleteNamed', { name: botName }))) {
      return;
    }
    try {
      await botService.deleteBot(botId);
      // Reload bots after deletion
      loadBots();
    } catch (error) {
      console.error('[ERROR] Failed to delete bot:', error);
      alert(t('admin.failedToDeleteAgent'));
    }
  };

  const loadAnalytics = async () => {
    // Load bots if not already loaded
    if (allBots.length === 0) {
      await loadBots();
    }
  };

  const loadBotDailyUsage = async (botId: string, days: number = analyticsTimeRange) => {
    try {
      setAnalyticsLoading(true);
      const usage = await groupAdminService.getBotDailyUsage(botId, days);
      setDailyUsageData(usage || []);
    } catch (error) {
      console.error('[ERROR] Failed to load daily usage:', error);
      setDailyUsageData([]);
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const handleAnalyticsBotChange = (botId: string) => {
    setSelectedAnalyticsBot(botId);
    // Reset brush selection when changing bot
    setBrushStart(null);
    setBrushEnd(null);
    if (botId) {
      loadBotDailyUsage(botId, analyticsTimeRange);
    } else {
      setDailyUsageData([]);
    }
  };

  const handleTimeRangeChange = (days: number) => {
    setAnalyticsTimeRange(days);
    // Reset brush selection when changing time range
    setBrushStart(null);
    setBrushEnd(null);
    if (selectedAnalyticsBot) {
      loadBotDailyUsage(selectedAnalyticsBot, days);
    }
  };

  const resetBrushSelection = () => {
    setBrushStart(null);
    setBrushEnd(null);
  };

  // Bot Insights functions
  const loadInsightsBots = async () => {
    try {
      // Use the allBots list if available, otherwise load bots
      if (allBots.length > 0) {
        const activeBots = allBots
          .filter(bot => bot.is_active)
          .map(bot => ({ chatbot_id: bot.chatbot_id, chatbot_name: bot.chatbot_name }));
        setInsightsBots(activeBots);
      } else {
        // Load bots from API
        const response = await groupAdminService.listBotAnalytics();
        const activeBots = response
          .filter((bot: BotAnalytics) => bot.is_active)
          .map((bot: BotAnalytics) => ({ chatbot_id: bot.chatbot_id, chatbot_name: bot.chatbot_name }));
        setInsightsBots(activeBots);
      }
    } catch (error) {
      console.error('[ERROR] Failed to load insights bots:', error);
      setInsightsBots([]);
    }
  };

  const loadBotSummaries = async (botId: string) => {
    try {
      setInsightsSummariesLoading(true);
      const summaries = await groupAdminService.getBotConversationSummaries(botId, 50);
      setInsightsSummaries(summaries);
    } catch (error) {
      console.error('[ERROR] Failed to load bot summaries:', error);
      setInsightsSummaries([]);
    } finally {
      setInsightsSummariesLoading(false);
    }
  };

  const handleInsightsBotChange = async (botId: string) => {
    setSelectedInsightsBot(botId);
    setInsightsMessages([]);
    setInsightsQuery('');
    if (botId) {
      await loadBotSummaries(botId);
    } else {
      setInsightsSummaries([]);
    }
  };

  // Ref for auto-scrolling insights chat
  const insightsMessagesEndRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (insightsMessagesEndRef.current) {
      insightsMessagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [insightsMessages, insightsLoading]);

  const handleInsightsSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!insightsQuery.trim() || !selectedInsightsBot || insightsSummaries.length === 0) return;

    const userMessage = insightsQuery.trim();
    setInsightsQuery('');
    const currentMessages = [...insightsMessages, { role: 'user' as const, content: userMessage }];
    setInsightsMessages(currentMessages);
    setInsightsLoading(true);

    try {
      // Build context from summaries
      const summariesContext = insightsSummaries.join('\n\n---\n\n');
      const systemPrompt = `You are an AI assistant helping analyze conversation summaries from an AI agent.
You are having a conversation with an admin who wants to understand patterns and insights from their agent's conversations.

IMPORTANT: This is a multi-turn conversation. Remember and reference our previous exchanges in this chat session when relevant. If the user asks follow-up questions or refers to something discussed earlier, use that context.

Based on the following ${insightsSummaries.length} conversation summaries, answer the user's questions.
Be specific and reference insights from the summaries when relevant.

CRITICAL PRIVACY RULES - YOU MUST FOLLOW THESE:
1. NEVER include any personal names, usernames, or identifiers in your responses
2. NEVER include email addresses, phone numbers, or any contact information
3. NEVER include specific user IDs or account identifiers
4. If a summary contains personal information, anonymize it (e.g., "a user" instead of "John Smith")
5. Focus on patterns, trends, and aggregate insights rather than individual cases
6. When discussing specific conversations, refer to them as "one user" or "a conversation" without identifying details

CONVERSATION SUMMARIES:
${summariesContext}`;

      // Call the AI API with full message history for context (excluding the current message which is sent separately)
      const response = await groupAdminService.askBotInsights(
        selectedInsightsBot,
        userMessage,
        systemPrompt,
        insightsMessages  // Pass all previous messages (before current one) for chat context
      );
      setInsightsMessages(prev => [...prev, { role: 'assistant', content: response }]);
    } catch (error) {
      console.error('[ERROR] Failed to get insights:', error);
      setInsightsMessages(prev => [...prev, { role: 'assistant', content: t('admin.insightsAnalysisError') }]);
    } finally {
      setInsightsLoading(false);
    }
  };

  const handleTabChange = (tabName: string) => {
    setCurrentTab(tabName);
    // Journey detections, inside the one funnel every tab change goes through.
    if (tabName === 'users') emitJourneyEvent(JOURNEY_EVENTS.adminUsersViewed);
    else if (tabName === 'hierarchy') emitJourneyEvent(JOURNEY_EVENTS.adminHierarchyViewed);
    if (tabName === 'users') {
      loadUsers();
      // The Add-User modal on this tab needs `tenants` for its dropdown. It
      // was only ever fetched on mount, so a transient failure there (or a
      // load that raced auth) left the dropdown permanently empty.
      loadHierarchy();
    } else if (tabName === 'bots') {
      loadBots();
    } else if (tabName === 'settings') {
      loadSettings();
    } else if (tabName === 'hierarchy' || tabName === 'tenants' || tabName === 'tenant-context'
               || tabName === 'system-design' || tabName === 'connector-library') {
      loadHierarchy();
    } else if (tabName === 'analytics') {
      loadAnalytics();
    } else if (tabName === 'insights') {
      loadInsightsBots();
    }
  };

  // Never let a previous attempt's error greet the admin on a fresh open.
  useEffect(() => {
    if (showCreateUserModal) setCreateUserError('');
    // Reset the tenant picker so it never reopens mid-search or stuck open.
    setIsUserTenantDropdownOpen(false);
    setUserTenantSearchQuery('');
  }, [showCreateUserModal]);

  useEffect(() => {
    if (showTenantUserModal) setTenantUserError('');
  }, [showTenantUserModal]);

  // When an error banner appears, smoothly bring it into view so the message is
  // visible even if the admin had scrolled the form down to submit.
  useEffect(() => {
    if (createUserError) {
      createUserErrorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [createUserError]);

  useEffect(() => {
    if (tenantUserError) {
      tenantUserErrorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [tenantUserError]);

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreateUserError('');
    if (!createUserForm.tenant_id) {
      setCreateUserError(t('admin.userManagement.selectTenantFirst', {
        defaultValue: 'Please choose the organization this user belongs to.',
      }));
      return;
    }

    if (createUserMode === 'password') {
      const policyError = validatePasswordPolicy(createUserForm.password);
      if (policyError) {
        setCreateUserError(t(policyError.key, { ...policyError.params, defaultValue: policyError.defaultValue }));
        return;
      }
    }

    setCreateUserSubmitting(true);
    try {
      if (createUserMode === 'invite') {
        const { email_sent } = await groupAdminService.inviteUser(createUserForm.tenant_id, {
          email: createUserForm.email,
          role: 'user',
          display_name: createUserForm.full_name || undefined,
        });
        message.success(email_sent
          ? t('admin.userManagement.invitationSent', { email: createUserForm.email, defaultValue: 'Invitation sent to {{email}}' })
          : t('admin.userManagement.invitationEmailFailed', { defaultValue: 'User created, but the invitation email failed — use "Resend invite" from the users table' }));
      } else {
        await groupAdminService.createUser(createUserForm.tenant_id, {
          email: createUserForm.email,
          password: createUserForm.password,
          role: 'user',  // Always create as user role - can be changed later
          display_name: createUserForm.full_name || undefined,
        });
      }
      if (createUserMode === 'password') {
        message.success(t('admin.userManagement.userCreated', {
          email: createUserForm.email,
          defaultValue: 'User {{email}} created',
        }));
      }
      setShowCreateUserModal(false);
      setCreateUserForm({ email: '', full_name: '', password: '', tenant_id: '' });
      setCreateUserMode('password');
      if (currentTab === 'users') {
        loadUsers();
      }
      loadDashboardData();
    } catch (error: unknown) {
      console.error('[ERROR] Failed to create user:', error);
      // Keep the modal open and surface the reason in place — the backend's
      // message already explains the duplicate-email case and names the tenant
      // the address is already used in.
      setCreateUserError(extractApiError(error, t('admin.userManagement.createUserFailed', { defaultValue: 'Failed to create user' })));
    } finally {
      setCreateUserSubmitting(false);
    }
  };

  // ── Password / invitation actions on an existing user ──────────────────

  const confirmSendResetEmail = (tenantId: string, user: TenantUser) => {
    Modal.confirm({
      title: t('admin.userManagement.sendResetConfirmTitle', { defaultValue: 'Send password reset email?' }),
      content: t('admin.userManagement.sendResetConfirmContent', { email: user.email, defaultValue: 'A reset link (valid for 1 hour) will be emailed to {{email}}.' }),
      okText: t('admin.userManagement.send', { defaultValue: 'Send' }),
      onOk: async () => {
        try {
          await groupAdminService.sendPasswordResetEmail(tenantId, user.user_id);
          message.success(t('admin.userManagement.resetLinkSent', { email: user.email, defaultValue: 'Reset link sent to {{email}}' }));
        } catch (error: any) {
          // A 5xx body is generic by design (request_guards): say it in the UI's own words.
          const res = error?.response;
          message.error((res?.status < 500 && res?.data?.error) || t('admin.userManagement.resetEmailFailed', { defaultValue: 'Failed to send the reset email' }));
        }
      },
    });
  };

  const handleResendInvite = async (tenantId: string, user: TenantUser) => {
    try {
      await groupAdminService.resendInvite(tenantId, user.user_id);
      message.success(t('admin.userManagement.invitationResent', { email: user.email, defaultValue: 'Invitation re-sent to {{email}}' }));
    } catch (error: any) {
      // A 5xx body is generic by design (request_guards): say it in the UI's own words.
      const res = error?.response;
      message.error((res?.status < 500 && res?.data?.error) || t('admin.userManagement.invitationResendFailed', { defaultValue: 'Failed to re-send the invitation' }));
    }
  };

  const openSetPasswordModal = (tenantId: string, user: TenantUser) => {
    setSetPasswordForm({ password: '', confirm: '' });
    setSetPasswordError('');
    setSetPasswordModal({ isOpen: true, tenantId, userId: user.user_id, userEmail: user.email });
  };

  const closeSetPasswordModal = () => {
    setSetPasswordModal({ isOpen: false, tenantId: '', userId: '', userEmail: '' });
    setSetPasswordForm({ password: '', confirm: '' });
    setSetPasswordError('');
  };

  const handleSetPasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const policyError = validatePasswordPolicy(setPasswordForm.password);
    if (policyError) {
      setSetPasswordError(t(policyError.key, { ...policyError.params, defaultValue: policyError.defaultValue }));
      return;
    }
    if (setPasswordForm.password !== setPasswordForm.confirm) {
      setSetPasswordError(t('admin.userManagement.passwordsDoNotMatch', { defaultValue: 'Passwords do not match' }));
      return;
    }
    setSetPasswordLoading(true);
    try {
      await groupAdminService.setUserPassword(
        setPasswordModal.tenantId, setPasswordModal.userId, setPasswordForm.password,
      );
      message.success(t('admin.userManagement.passwordUpdated', { email: setPasswordModal.userEmail, defaultValue: 'Password updated for {{email}}' }));
      closeSetPasswordModal();
      // Pending badge may have cleared — refresh whichever list is visible.
      if (selectedTenantId) {
        const users = await groupAdminService.listUsers(selectedTenantId);
        setTenantUsers(users);
      }
      if (currentTab === 'users') {
        loadUsers();
      }
    } catch (error: any) {
      setSetPasswordError(error?.response?.data?.error || t('admin.userManagement.setPasswordFailed', { defaultValue: 'Failed to set password' }));
    } finally {
      setSetPasswordLoading(false);
    }
  };

  // Open delete confirmation modal for user (from Users tab)
  const openDeleteUserModal = (userId: string, userName: string) => {
    setDeleteModal({
      isOpen: true,
      type: 'user',
      source: 'users-tab',
      id: userId,
      name: userName,
      loading: false,
    });
  };

  // Confirm delete user (from Users tab)
  const handleDeleteUser = async () => {
    if (!deleteModal.id || deleteModal.type !== 'user') return;

    const targetName = deleteModal.name;
    setDeleteModal(prev => ({ ...prev, loading: true }));

    try {
      // Find the user's tenant_id from the allUsers list
      const user = allUsers.find(u => u.user_id === deleteModal.id);
      if (user?.tenant_id) {
        await groupAdminService.deleteUser(user.tenant_id, deleteModal.id);
      } else {
        // Fallback to admin service for current tenant
        await adminService.deleteUser(deleteModal.id);
      }
      setDeleteModal({ isOpen: false, type: null, source: null, id: '', name: '', loading: false });
      message.success(t('admin.userDeletedNamed', { name: targetName }));
      await loadUsers();
      await loadDashboardData();
    } catch (error: unknown) {
      console.error('[ERROR] Failed to delete user:', error);
      let detail = t('common.unknownError');
      if (error && typeof error === 'object') {
        const axiosError = error as { response?: { data?: { error?: string } }; message?: string };
        detail = axiosError.response?.data?.error || axiosError.message || detail;
      }
      message.error(t('admin.deleteUserFailedDetail', { detail }));
      setDeleteModal(prev => ({ ...prev, loading: false }));
    }
  };

  const handleToggleUserStatus = async (userId: string) => {
    try {
      // Find the user from allUsers to get tenant_id and current status
      const user = allUsers.find(u => u.user_id === userId);
      if (!user) {
        console.error('[ERROR] User not found:', userId);
        return;
      }

      if (user.tenant_id) {
        await groupAdminService.updateUser(user.tenant_id, userId, { is_active: !user.is_active });
      } else {
        // Fallback to admin service for current tenant - pass current active state
        await adminService.toggleUserStatus(userId, user.is_active);
      }
      loadUsers();
      loadDashboardData();
    } catch (error) {
      console.error('[ERROR] Failed to toggle user status:', error);
    }
  };

  const handleUserRoleChange = async (userId: string, newRole: string) => {
    try {
      // Validate role type
      const validRoles = ['user', 'admin', 'group_admin', 'super_admin'] as const;
      if (!validRoles.includes(newRole as typeof validRoles[number])) {
        console.error('[ERROR] Invalid role:', newRole);
        return;
      }

      // Find the user from allUsers to get tenant_id
      const user = allUsers.find(u => u.user_id === userId);
      if (user?.tenant_id) {
        await groupAdminService.updateUser(user.tenant_id, userId, { role: newRole as 'user' | 'admin' | 'group_admin' | 'super_admin' });
      } else {
        // Fallback to admin service for current tenant
        await adminService.updateUserRole(userId, newRole);
      }
      loadUsers();
    } catch (error) {
      console.error('[ERROR] Failed to update user role:', error);
    }
  };

  const handleUserTierChange = async (userId: string, tier: number) => {
    try {
      const user = allUsers.find(u => u.user_id === userId);
      if (user?.tenant_id) {
        await groupAdminService.updateUser(user.tenant_id, userId, { user_tier: tier });
      }
      loadUsers();
    } catch (error) {
      console.error('[ERROR] Failed to update user tier:', error);
    }
  };

  const handleSaveTenantPrompt = async () => {
    try {
      setSettingsLoading(true);
      await adminService.updateTenantSettings({ tenant_prompt: tenantPrompt });
      alert(t('admin.tenantPromptSaved'));
    } catch (error) {
      console.error('[ERROR] Failed to save tenant prompt:', error);
      alert(t('admin.tenantPromptSaveFailed'));
    } finally {
      setSettingsLoading(false);
    }
  };

  const toggleNodeCollapse = (tenantId: string) => {
    const newCollapsed = new Set(collapsedNodes);
    if (newCollapsed.has(tenantId)) {
      newCollapsed.delete(tenantId);
    } else {
      newCollapsed.add(tenantId);
    }
    setCollapsedNodes(newCollapsed);
  };

  const handleTenantSelect = async (tenantId: string, tenantName: string, hasChildren: boolean = false, isRoot: boolean = false) => {
    setSelectedTenantId(tenantId);
    setSelectedTenantName(tenantName);
    setSelectedTenantHasChildren(hasChildren);
    setSelectedTenantIsRoot(isRoot);
    setTenantUsersLoading(true);
    try {
      const users = await groupAdminService.listUsers(tenantId);
      setTenantUsers(users);
    } catch (error) {
      console.error('[ERROR] Failed to load tenant users:', error);
      setTenantUsers([]);
    } finally {
      setTenantUsersLoading(false);
    }
  };

  // Create user in selected tenant (Organization tab)
  const handleCreateTenantUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setTenantUserError('');
    if (!selectedTenantId) return;

    if (tenantUserMode === 'password') {
      const policyError = validatePasswordPolicy(tenantUserForm.password);
      if (policyError) {
        setTenantUserError(t(policyError.key, { ...policyError.params, defaultValue: policyError.defaultValue }));
        return;
      }
    }

    setTenantUserSubmitting(true);
    try {
      if (tenantUserMode === 'invite') {
        const { email_sent } = await groupAdminService.inviteUser(selectedTenantId, {
          email: tenantUserForm.email,
          role: 'user',
          display_name: tenantUserForm.display_name || undefined,
        });
        message.success(email_sent
          ? t('admin.userManagement.invitationSent', { email: tenantUserForm.email, defaultValue: 'Invitation sent to {{email}}' })
          : t('admin.userManagement.invitationEmailFailed', { defaultValue: 'User created, but the invitation email failed — use "Resend invite" from the users table' }));
      } else {
        await groupAdminService.createUser(selectedTenantId, {
          email: tenantUserForm.email,
          password: tenantUserForm.password,
          role: 'user',  // Always create as user role - can be changed later
          display_name: tenantUserForm.display_name || undefined,
        });
      }
      if (tenantUserMode === 'password') {
        message.success(t('admin.userManagement.userCreated', {
          email: tenantUserForm.email,
          defaultValue: 'User {{email}} created',
        }));
      }
      setShowTenantUserModal(false);
      setTenantUserForm({ email: '', display_name: '', password: '' });
      setTenantUserMode('password');
      // Reload tenant users
      const users = await groupAdminService.listUsers(selectedTenantId);
      setTenantUsers(users);
      // Reload hierarchy to update user counts
      loadHierarchy();
    } catch (error: unknown) {
      console.error('[ERROR] Failed to create tenant user:', error);
      // Stay open and explain in place. The duplicate-email message names the
      // tenant the address is already registered under.
      setTenantUserError(extractApiError(error, t('admin.userManagement.createUserFailed', { defaultValue: 'Failed to create user' })));
    } finally {
      setTenantUserSubmitting(false);
    }
  };

  // Open delete confirmation modal for tenant user
  const openDeleteTenantUserModal = (userId: string, userName: string) => {
    setDeleteModal({
      isOpen: true,
      type: 'user',
      source: 'org-tab',
      id: userId,
      name: userName,
      loading: false,
    });
  };

  // Delete user from tenant (Organization tab)
  const handleDeleteTenantUser = async () => {
    if (!selectedTenantId || !deleteModal.id) return;

    setDeleteModal(prev => ({ ...prev, loading: true }));

    try {
      await groupAdminService.deleteUser(selectedTenantId, deleteModal.id);
      // Close modal
      setDeleteModal({ isOpen: false, type: null, source: null, id: '', name: '', loading: false });
      // Reload tenant users
      const users = await groupAdminService.listUsers(selectedTenantId);
      setTenantUsers(users);
      // Reload hierarchy to update user counts
      await loadHierarchy();
    } catch (error) {
      console.error('[ERROR] Failed to delete tenant user:', error);
      setDeleteModal(prev => ({ ...prev, loading: false }));
      alert(t('admin.failedToDeleteUser'));
    }
  };

  // Toggle user active status in tenant (Organization tab)
  const handleToggleTenantUserStatus = async (userId: string) => {
    if (!selectedTenantId) return;

    const user = tenantUsers.find(u => u.user_id === userId);
    if (!user) return;

    try {
      await groupAdminService.updateUser(selectedTenantId, userId, { is_active: !user.is_active });
      // Reload tenant users
      const users = await groupAdminService.listUsers(selectedTenantId);
      setTenantUsers(users);
    } catch (error) {
      console.error('[ERROR] Failed to toggle tenant user status:', error);
    }
  };

  // Toggle tenant status (active/inactive) - users in inactive tenant cannot login
  const handleToggleTenantStatus = async (tenantId: string) => {
    const tenant = tenants.find(t => t.tenant_id === tenantId);
    if (!tenant) return;

    const newStatus = tenant.tenant_status === 'active' ? 'inactive' : 'active';

    try {
      await groupAdminService.updateTenant(tenantId, { tenant_status: newStatus });
      // Reload hierarchy to get updated tenant list
      loadHierarchy();
    } catch (error) {
      console.error('[ERROR] Failed to toggle tenant status:', error);
    }
  };

  const handleDeleteTenant = async (tenantId: string, tenantName: string) => {
    if (!window.confirm(t('admin.confirmDeleteTenant', { name: tenantName }))) {
      return;
    }

    try {
      await groupAdminService.deleteTenant(tenantId);
      // Reload hierarchy to get updated tenant list
      loadHierarchy();
    } catch (error: unknown) {
      console.error('[ERROR] Failed to delete tenant:', error);
      const errorMessage = error instanceof Error ? error.message : t('common.unknownError');
      alert(t('admin.deleteTenantFailed', { detail: errorMessage }));
    }
  };

  const handleTenantUserRoleChange = async (userId: string, newRole: string, tenantId?: string) => {
    // Find the tenant_id from the user if not provided
    const targetTenantId = tenantId ||
      tenantUsers.find(u => u.user_id === userId)?.tenant_id ||
      allUsers.find(u => u.user_id === userId)?.tenant_id ||
      selectedTenantId;

    if (!targetTenantId) {
      console.error('[ERROR] No tenant ID found for user');
      return;
    }

    try {
      await groupAdminService.updateUser(targetTenantId, userId, { role: newRole as 'user' | 'admin' | 'group_admin' | 'super_admin' });
      // Reload the appropriate user list
      if (selectedTenantId) {
        const users = await groupAdminService.listUsers(selectedTenantId);
        setTenantUsers(users);
      }
      // Also reload the all users list if we're on the Users tab
      if (currentTab === 'users') {
        loadUsers();
      }
    } catch (error) {
      console.error('[ERROR] Failed to update user role:', error);
    }
  };

  // Open create tenant modal with proper default parent based on role
  const openCreateTenantModal = () => {
    // Set default parent tenant based on role:
    // - Group Admin: Must use their own tenant as parent
    // - Super Admin: Default to root tenant (first tenant with is_root_tenant=true)
    let defaultParentId = '';

    if (isGroupAdmin && !isSuperAdmin) {
      // Group admin must use their own tenant
      defaultParentId = currentUserTenantId || '';
    } else if (isSuperAdmin) {
      // Super admin defaults to root tenant
      const rootTenant = tenants.find(t => t.is_root_tenant);
      defaultParentId = rootTenant?.tenant_id || '';
    }

    setCreateTenantForm({
      tenant_name: '',
      tenant_code: '',
      parent_tenant_id: defaultParentId,
    });
    setShowCreateTenantModal(true);
  };

  // Create a new tenant
  const handleCreateTenant = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const payload: {
        tenant_name: string;
        tenant_code: string;
        parent_tenant_id?: string;
      } = {
        tenant_name: createTenantForm.tenant_name,
        tenant_code: createTenantForm.tenant_code,
      };

      // Add parent tenant if selected
      if (createTenantForm.parent_tenant_id) {
        payload.parent_tenant_id = createTenantForm.parent_tenant_id;
      }

      await groupAdminService.createTenant(payload);
      setShowCreateTenantModal(false);
      setCreateTenantForm({
        tenant_name: '',
        tenant_code: '',
        parent_tenant_id: '',
      });
      // Reload hierarchy to show new tenant
      loadHierarchy();
    } catch (error: unknown) {
      console.error('[ERROR] Failed to create tenant:', error);
      // Extract error message from Axios error response or Error object
      let errorMessage = t('common.unknownError');
      if (error && typeof error === 'object') {
        // Check for Axios error response
        const axiosError = error as { response?: { data?: { error?: string } } };
        if (axiosError.response?.data?.error) {
          errorMessage = axiosError.response.data.error;
        } else if (error instanceof Error) {
          errorMessage = error.message;
        }
      }
      alert(`Failed to create tenant: ${errorMessage}`);
    }
  };

  // Auto-generate tenant code from tenant name (with debounce and uniqueness check)
  const codeGenerationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleTenantNameChange = (name: string) => {
    setCreateTenantForm(prev => ({ ...prev, tenant_name: name }));

    // Clear any pending code generation
    if (codeGenerationTimeoutRef.current) {
      clearTimeout(codeGenerationTimeoutRef.current);
    }

    if (name.trim()) {
      // Debounce: wait 300ms after user stops typing
      codeGenerationTimeoutRef.current = setTimeout(async () => {
        try {
          const generatedCode = await groupAdminService.generateTenantCode(name);
          setCreateTenantForm(prev => ({ ...prev, tenant_code: generatedCode }));
        } catch (error) {
          console.error('[ERROR] Failed to generate tenant code:', error);
          // Fallback to local generation if API fails
          const localCode = name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
          setCreateTenantForm(prev => ({ ...prev, tenant_code: localCode }));
        }
      }, 300);
    }
  };

  // Helper function to create numeric filter dropdown (used for all numeric columns)
  const createNumericFilterDropdown = ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: FilterDropdownProps) => {
    const filterValue = selectedKeys[0] ? String(selectedKeys[0]).split(':') : ['gte', '', ''];
    const mode = filterValue[0] || 'gte';
    const value1 = filterValue[1] || '';
    const value2 = filterValue[2] || '';

    const updateFilter = (newMode: string, newValue1: string, newValue2: string) => {
      setSelectedKeys([`${newMode}:${newValue1}:${newValue2}`]);
      // Auto-apply filter on value change (like text filters)
      if (newValue1 || newValue2) {
        confirm({ closeDropdown: false });
      }
    };

    return (
      <div style={{ padding: 8, width: 200 }}>
        <Radio.Group
          value={mode}
          onChange={(e) => updateFilter(e.target.value, value1, value2)}
          style={{ marginBottom: 8, display: 'block' }}
        >
          <Radio value="exact">{t('admin.filterExact')}</Radio>
          <Radio value="gte">{t('admin.filterGreaterOrEqual')}</Radio>
          <Radio value="lte">{t('admin.filterLessOrEqual')}</Radio>
          <Radio value="range">{t('admin.filterRange')}</Radio>
        </Radio.Group>
        {mode === 'exact' && (
          <Input dir="auto"
            placeholder={t('admin.filterNumber')}
            value={value1}
            onChange={(e) => updateFilter(mode, e.target.value, value2)}
            onPressEnter={() => confirm()}
            style={{ marginBottom: 8 }}
          />
        )}
        {mode === 'gte' && (
          <Input dir="auto"
            placeholder={t('admin.filterMinValue')}
            value={value1}
            onChange={(e) => updateFilter(mode, e.target.value, value2)}
            onPressEnter={() => confirm()}
            style={{ marginBottom: 8 }}
          />
        )}
        {mode === 'lte' && (
          <Input dir="auto"
            placeholder={t('admin.filterMaxValue')}
            value={value1}
            onChange={(e) => updateFilter(mode, e.target.value, value2)}
            onPressEnter={() => confirm()}
            style={{ marginBottom: 8 }}
          />
        )}
        {mode === 'range' && (
          <>
            <Input dir="auto"
              placeholder={t('admin.filterMin')}
              value={value1}
              onChange={(e) => updateFilter(mode, e.target.value, value2)}
              onPressEnter={() => confirm()}
              style={{ marginBottom: 8 }}
            />
            <Input dir="auto"
              placeholder={t('admin.filterMax')}
              value={value2}
              onChange={(e) => updateFilter(mode, value1, e.target.value)}
              onPressEnter={() => confirm()}
              style={{ marginBottom: 8 }}
            />
          </>
        )}
        <Space>
          <Button onClick={() => { clearFilters?.(); confirm(); }} size="small">
            {t('common.reset')}
          </Button>
        </Space>
      </div>
    );
  };

  // Helper function for numeric filter onFilter logic
  const createNumericOnFilter = (getValue: (record: any) => number) => (value: any, record: any) => {
    if (!value) return true;
    const count = getValue(record);
    const parts = String(value).split(':');
    const mode = parts[0];
    const value1Str = parts[1] || '';
    const value2Str = parts[2] || '';

    if (mode === 'exact') {
      if (!value1Str || value1Str === '') return true;
      const searchNum = parseInt(value1Str, 10);
      if (isNaN(searchNum)) return true;
      return count === searchNum;
    } else if (mode === 'gte') {
      if (!value1Str || value1Str === '') return true;
      const searchNum = parseInt(value1Str, 10);
      if (isNaN(searchNum)) return true;
      return count >= searchNum;
    } else if (mode === 'lte') {
      if (!value1Str || value1Str === '') return true;
      const searchNum = parseInt(value1Str, 10);
      if (isNaN(searchNum)) return true;
      return count <= searchNum;
    } else if (mode === 'range') {
      if ((!value1Str || value1Str === '') && (!value2Str || value2Str === '')) return true;
      const min = value1Str && value1Str !== '' ? parseInt(value1Str, 10) : Number.MIN_SAFE_INTEGER;
      const max = value2Str && value2Str !== '' ? parseInt(value2Str, 10) : Number.MAX_SAFE_INTEGER;
      if (isNaN(min) && isNaN(max)) return true;
      return count >= (isNaN(min) ? Number.MIN_SAFE_INTEGER : min) && count <= (isNaN(max) ? Number.MAX_SAFE_INTEGER : max);
    }
    return true;
  };

  // Resizable column component - supports both fixed width and auto-sized columns
  const ResizableTitle = (props: any) => {
    const { onResize, width, ...restProps } = props;
    const thRef = useRef<HTMLTableCellElement>(null);

    // If no onResize callback, just render plain th
    if (!onResize) {
      return <th {...restProps} />;
    }

    return (
      <th
        ref={thRef}
        {...restProps}
        style={{ ...restProps.style, position: 'relative', userSelect: 'none', width: width || 'auto' }}
        onMouseDown={(e) => {
          // Only trigger resize from the resize handle area (right 10px)
          const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
          if (e.clientX < rect.right - 10) return;

          e.preventDefault();
          const startX = e.clientX;
          // Use provided width or get current width from DOM
          const startWidth = width || thRef.current?.offsetWidth || 100;

          const handleMouseMove = (moveEvent: MouseEvent) => {
            const newWidth = startWidth + (moveEvent.clientX - startX);
            if (newWidth >= 140) { // Minimum column width - ensures text remains visible with icons
              onResize(newWidth);
            }
          };

          const handleMouseUp = () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            document.body.style.cursor = 'default';
            document.body.style.userSelect = 'auto';
          };

          document.addEventListener('mousemove', handleMouseMove);
          document.addEventListener('mouseup', handleMouseUp);
          document.body.style.cursor = 'col-resize';
          document.body.style.userSelect = 'none';
        }}
      >
        {restProps.children}
        <div
          style={{
            position: 'absolute',
            right: 0,
            top: 0,
            bottom: 0,
            width: '10px',
            cursor: 'col-resize',
            background: 'transparent',
            zIndex: 1
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'rgba(59, 130, 246, 0.3)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'transparent';
          }}
        />
      </th>
    );
  };

  return (
    <div className="admin-container">
      <Sidebar
        options={{
          showHome: true,
          showChat: false,
          showBots: true,
          showSearch: true,
          showAdmin: true,
          showSuperAdmin: false,
          showConversations: false,
          showBotsList: false,
          activeItem: 'admin',
        }}
      />

      <div className="main-container">
        <main className="main-content">
          {/* Header */}
          <div className="simple-chat-header">
            <h1 dir="auto" className="bot-title">{t('admin.adminDashboard')}</h1>
            <p className="text-muted">{t('admin.tenantManagement')}</p>
          </div>

          {/* Tab Navigation */}
          <div className="admin-tabs">
            {/* Dashboard tab hidden for now */}
            {/* <button
              className={`tab-btn ${currentTab === 'dashboard' ? 'active' : ''}`}
              onClick={() => handleTabChange('dashboard')}
            >
              {t('nav.dashboard')}
            </button> */}
            <button
              className={`tab-btn ${currentTab === 'hierarchy' ? 'active' : ''}`}
              data-testid="admin-tab-hierarchy"
              onClick={() => handleTabChange('hierarchy')}
            >
              {t('vault.section.org')}
            </button>
            <button
              className={`tab-btn ${currentTab === 'tenants' ? 'active' : ''}`}
              data-testid="admin-tab-tenants"
              onClick={() => handleTabChange('tenants')}
            >
              {t('admin.tenants')}
            </button>
            <button
              className={`tab-btn ${currentTab === 'users' ? 'active' : ''}`}
              data-testid="admin-tab-users"
              onClick={() => handleTabChange('users')}
            >
              {t('admin.users')}
            </button>
            <button
              className={`tab-btn ${currentTab === 'bots' ? 'active' : ''}`}
              data-testid="admin-tab-bots"
              onClick={() => handleTabChange('bots')}
            >
              {t('nav.agents')}
            </button>
            {/* Skills, Analytics and Agent Insights tabs hidden for now */}
            {(isSuperAdmin || isGroupAdmin) && tenantContextFeatureEnabled && (
              <button
                className={`tab-btn ${currentTab === 'tenant-context' ? 'active' : ''}`}
                onClick={() => handleTabChange('tenant-context')}
              >
                {t('groupAdmin.tabs.context')}
              </button>
            )}
            {/* Brand book for the subtree — group admins and super admins
                only. Admins and users never see this tab. */}
            {(isSuperAdmin || isGroupAdmin) && (
              <button
                className={`tab-btn ${currentTab === 'system-design' ? 'active' : ''}`}
                onClick={() => handleTabChange('system-design')}
                data-testid="system-design-tab-btn"
              >
                {t('systemDesign.tabLabel')}
              </button>
            )}
            {libraryTabAllowed && (
              <button
                className={`tab-btn ${currentTab === 'connector-library' ? 'active' : ''}`}
                onClick={() => handleTabChange('connector-library')}
                data-testid="connector-library-tab-btn"
              >
                {t('connectorLibrary.title')}
              </button>
            )}
          </div>

          {/* Dashboard Tab */}
          {currentTab === 'dashboard' && (
            <div className="tab-content active">
              {/* Quick Actions */}
              <div className="actions-grid">
                <div
                  className="action-card card"
                  onClick={() => setShowCreateUserModal(true)}
                >
                  <div className="action-icon">
                    <svg viewBox="0 0 24 24" width="24" height="24">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                      <circle cx="12" cy="7" r="4" />
                    </svg>
                  </div>
                  <div className="action-content">
                    <h3>{t('admin.createUser')}</h3>
                    <p>{t('admin.createUserDescription')}</p>
                  </div>
                </div>

                <div
                  className="action-card card"
                  onClick={() => handleTabChange('settings')}
                >
                  <div className="action-icon">
                    <svg viewBox="0 0 24 24" width="24" height="24">
                      <circle cx="12" cy="12" r="3" />
                      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
                    </svg>
                  </div>
                  <div className="action-content">
                    <h3>{t('admin.tenantSettings')}</h3>
                    <p>{t('admin.tenantSettingsDescription')}</p>
                  </div>
                </div>

                {/* Analytics card hidden for now */}
              </div>

              {/* Stats Cards */}
              <div className="stats-grid">
                <StatsCard
                  icon={
                    <svg viewBox="0 0 24 24">
                      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                      <circle cx="9" cy="7" r="4" />
                      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                    </svg>
                  }
                  value={stats.total_users}
                  label={t('admin.totalUsers')}
                  description={t('admin.allRegisteredUsers')}
                  onClick={() => handleTabChange('users')}
                />

                <StatsCard
                  icon={
                    <svg viewBox="0 0 24 24">
                      <path d="M9 12l2 2 4-4" />
                      <circle cx="12" cy="12" r="10" />
                    </svg>
                  }
                  value={stats.active_users}
                  label={t('admin.activeUsers')}
                  description={t('admin.enabledAccounts')}
                />

                <StatsCard
                  icon={
                    <svg viewBox="0 0 24 24">
                      <path d="M21 15.5c0 2.485-2.015 4.5-4.5 4.5H8l-4 3v-13.5C4 7.015 6.015 5 8.5 5h8c2.485 0 4.5 2.015 4.5 4.5v6z" />
                    </svg>
                  }
                  value={stats.total_conversations}
                  label={t('admin.conversations')}
                  description={t('admin.thisMonth')}
                />

                <StatsCard
                  icon={
                    <svg viewBox="0 0 24 24">
                      <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                      <circle cx="9" cy="9" r="2" />
                      <path d="M21 15l-3.086-3.086a2 2 0 0 0-2.828 0L6 21" />
                    </svg>
                  }
                  value={stats.total_messages}
                  label={t('admin.messages')}
                  description={t('admin.thisMonth')}
                />
              </div>

              {/* Recent Users */}
              <div className="activity-section">
                <div className="card">
                  <div className="card-header">
                    <h3>{t('admin.recentUsers')}</h3>
                    <button
                      className="btn-ghost"
                      onClick={() => handleTabChange('users')}
                    >
                      {t('admin.viewAll')}
                    </button>
                  </div>
                  <div className="table-container">
                    <table className="modern-table">
                      <thead>
                        <tr>
                          <th>{t('admin.user')}</th>
                          <th>{t('admin.role')}</th>
                          <th>{t('admin.status')}</th>
                          <th>{t('admin.created')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {loading ? (
                          <tr>
                            <td colSpan={4} className="loading-cell">
                              {t('admin.loadingUsers')}
                            </td>
                          </tr>
                        ) : recentUsers.length === 0 ? (
                          <tr>
                            <td colSpan={4} className="loading-cell">
                              {t('admin.noUsersYet')}
                            </td>
                          </tr>
                        ) : (
                          recentUsers.map((user) => (
                            <tr key={user.user_id}>
                              <td>
                                <div className="user-info">
                                  <div className="user-avatar-small" dir="auto">
                                    {getInitials((user as any).display_name || (user as any).full_name || user.email)}
                                  </div>
                                  <div>
                                    <div className="user-name" dir="auto">
                                      {user.full_name || user.email}
                                    </div>
                                    <div className="user-email-small">{user.email}</div>
                                  </div>
                                </div>
                              </td>
                              <td>
                                <span
                                  className={`role-badge role-${user.role}`}
                                >
                                  {user.role}
                                </span>
                              </td>
                              <td>
                                <span
                                  className={`status-badge ${
                                    user.is_active ? 'badge-success' : 'badge-error'
                                  }`}
                                >
                                  {user.is_active ? t('common.active') : t('common.inactive')}
                                </span>
                              </td>
                              <td className="user-date">
                                {new Date(user.created_at).toLocaleDateString(activeLocale())}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Organization Hierarchy Tab */}
          {currentTab === 'hierarchy' && (
            <div className="tab-content active">
              <div className="hierarchy-section">
                <div className="hierarchy-layout-fixed">
                  {/* Hierarchy Tree */}
                  <div className="card hierarchy-card-fixed">
                    <div className="card-header">
                      <h3>{t('admin.tenantStructure')}</h3>
                      <div className="hierarchy-actions">
                        {(isSuperAdmin || isGroupAdmin) && (
                          <button
                            className="hierarchy-action-btn hierarchy-action-primary"
                            onClick={openCreateTenantModal}
                          >
                            <svg viewBox="0 0 24 24" width="16" height="16">
                              <path d="M12 5v14" />
                              <path d="M5 12h14" />
                            </svg>
                            {t('admin.newTenant')}
                          </button>
                        )}
                        <button
                          className="hierarchy-action-btn hierarchy-action-refresh"
                          onClick={loadHierarchy}
                          title={t('admin.refresh')}
                        >
                          <svg viewBox="0 0 24 24" width="16" height="16">
                            <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
                            <path d="M21 3v5h-5" />
                            <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
                            <path d="M8 16l-5 5v-5h5" />
                          </svg>
                        </button>
                      </div>
                    </div>

                    <div className="card-content">
                      {hierarchyLoading ? (
                        <div className="loading-cell" style={{ padding: '40px', textAlign: 'center' }}>
                          {t('admin.loadingOrgStructure')}
                        </div>
                      ) : hierarchyTree ? (
                        <div className="hierarchy-tree">
                          <HierarchyTreeNode
                            node={hierarchyTree}
                            onSelect={handleTenantSelect}
                            selectedTenantId={selectedTenantId || undefined}
                            collapsedNodes={collapsedNodes}
                            onToggleCollapse={toggleNodeCollapse}
                          />
                        </div>
                      ) : (
                        <div className="loading-cell" style={{ padding: '40px', textAlign: 'center' }}>
                          {t('admin.noHierarchyData')}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Selected Tenant Users */}
                  <div className="card tenant-users-card-fixed">
                    <div className="card-header">
                      <h3>
                        {selectedTenantName ? t('admin.usersInTenant', { tenant: selectedTenantName }) : t('admin.tenantUsers')}
                      </h3>
                      {selectedTenantId && (
                        <button
                          className="hierarchy-action-btn hierarchy-action-primary"
                          onClick={() => setShowTenantUserModal(true)}
                        >
                          <svg viewBox="0 0 24 24" width="16" height="16">
                            <path d="M12 5v14" />
                            <path d="M5 12h14" />
                          </svg>
                          {t('admin.addUser')}
                        </button>
                      )}
                    </div>
                    <div className="card-content">
                      {!selectedTenantId ? (
                        <div className="empty-state" style={{ padding: '40px', textAlign: 'center' }}>
                          <svg viewBox="0 0 24 24" width="48" height="48" style={{ color: 'var(--text-muted)', marginBottom: '16px' }}>
                            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" fill="none" stroke="currentColor" strokeWidth="2" />
                            <circle cx="12" cy="7" r="4" fill="none" stroke="currentColor" strokeWidth="2" />
                          </svg>
                          <p style={{ color: 'var(--text-muted)' }}>{t('admin.selectTenantFromHierarchy')}</p>
                        </div>
                      ) : tenantUsersLoading ? (
                        <div className="loading-cell" style={{ padding: '40px', textAlign: 'center' }}>
                          {t('admin.loadingUsers')}
                        </div>
                      ) : (
                        <div style={{ padding: 'var(--space-4)' }} ref={orgUsersWrapRef}>
                          <Table<TenantUser>
                            components={{
                              header: {
                                cell: ResizableTitle,
                              },
                            }}
                            columns={[
                              {
                                title: <div style={{ textAlign: 'center' }}>{t('admin.user')}</div>,
                                dataIndex: 'email',
                                key: 'email',
                                width: orgUsersColumnWidths.email,
                                onHeaderCell: () => ({
                                  width: orgUsersColumnWidths.email,
                                  onResize: (width: number) => setOrgUsersColumnWidths(prev => ({ ...prev, email: width })),
                                }),
                                render: (email: string, user: TenantUser) => (
                                  <div className="user-info">
                                    <div className="user-avatar">{getInitials((user as any).display_name || (user as any).full_name || email)}</div>
                                    <div className="user-details">
                                      <div className="user-name" dir="auto">{user.display_name || email}</div>
                                      <div className="user-email">{email}</div>
                                    </div>
                                  </div>
                                ),
                              },
                              {
                                title: <div style={{ textAlign: 'center' }}>{t('admin.role')}</div>,
                                dataIndex: 'role',
                                key: 'role',
                                width: orgUsersColumnWidths.role,
                                align: 'center' as const,
                                onHeaderCell: () => ({
                                  width: orgUsersColumnWidths.role,
                                  onResize: (width: number) => setOrgUsersColumnWidths(prev => ({ ...prev, role: width })),
                                }),
                                render: (role: string, user: TenantUser) => {
                                  const roleLabels: Record<string, string> = {
                                    user: t('admin.user'),
                                    admin: t('nav.admin'),
                                    group_admin: t('nav.groupAdmin'),
                                    super_admin: t('nav.superAdmin'),
                                  };

                                  // Build menu items based on what roles can actually be assigned
                                  const menuItems: { key: string; label: string }[] = [];

                                  // Super admin can assign ANY role to ANYONE - no restrictions
                                  if (isSuperAdmin) {
                                    menuItems.push({ key: 'user', label: t('admin.user') });
                                    menuItems.push({ key: 'admin', label: t('nav.admin') });
                                    menuItems.push({ key: 'group_admin', label: t('nav.groupAdmin') });
                                    menuItems.push({ key: 'super_admin', label: t('nav.superAdmin') });
                                  } else if (isGroupAdmin || isAdmin) {
                                    // Non-super admins have limited options
                                    menuItems.push({ key: 'user', label: t('admin.user') });
                                    menuItems.push({ key: 'admin', label: t('nav.admin') });
                                    // group_admin can assign group_admin in own tenant and descendant tenants
                                    if (isGroupAdmin) {
                                      menuItems.push({ key: 'group_admin', label: t('nav.groupAdmin') });
                                    }
                                  }

                                  return (
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', width: 'fit-content', margin: '0 auto' }}>
                                      <span className={`role-badge role-${role}`}>{roleLabels[role] || role}</span>
                                      <Dropdown
                                        trigger={['click']}
                                        disabled={user.user_id === currentUserId}
                                        menu={{
                                          items: menuItems,
                                          onClick: ({ key }) => handleTenantUserRoleChange(user.user_id, key),
                                        }}
                                      >
                                        <Button
                                          type="text"
                                          size="small"
                                          icon={<EditOutlined />}
                                          disabled={user.user_id === currentUserId}
                                          style={{ padding: '2px 4px', height: 'auto' }}
                                        />
                                      </Dropdown>
                                    </div>
                                  );
                                },
                              },
                              {
                                title: <div style={{ textAlign: 'center' }}>{t('admin.status')}</div>,
                                dataIndex: 'is_active',
                                key: 'is_active',
                                width: orgUsersColumnWidths.is_active,
                                align: 'center' as const,
                                onHeaderCell: () => ({
                                  width: orgUsersColumnWidths.is_active,
                                  onResize: (width: number) => setOrgUsersColumnWidths(prev => ({ ...prev, is_active: width })),
                                }),
                                render: (is_active: boolean, user: TenantUser) => (
                                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                    <label className={`status-switch ${user.user_id === currentUserId ? 'disabled' : ''}`}>
                                      <input
                                        type="checkbox"
                                        checked={is_active}
                                        onChange={() => handleToggleTenantUserStatus(user.user_id)}
                                        disabled={user.user_id === currentUserId}
                                      />
                                      <span className="switch-slider"></span>
                                      <span className="switch-label">{is_active ? t('common.active') : t('common.inactive')}</span>
                                    </label>
                                    {user.has_password === false && (
                                      <Tag color="gold" title={t('admin.userManagement.pendingTooltip', { defaultValue: "Invited — hasn't set a password yet" })}>{t('admin.userManagement.pending', { defaultValue: 'Pending' })}</Tag>
                                    )}
                                  </span>
                                ),
                              },
                              {
                                title: <div style={{ textAlign: 'center' }}>{t('admin.actions')}</div>,
                                key: 'actions',
                                align: 'center' as const,
                                width: orgUsersColumnWidths.actions,
                                onHeaderCell: () => ({
                                  width: orgUsersColumnWidths.actions,
                                  onResize: (width: number) => setOrgUsersColumnWidths(prev => ({ ...prev, actions: width })),
                                }),
                                render: (_, user) => {
                                  const isProtectedTarget = user.role === 'super_admin' && !isSuperAdmin;
                                  return (
                                    <div className="user-actions">
                                      {user.has_password === false && canInvite && rowEmailEnabled(user) && (
                                        <Button
                                          type="text"
                                          icon={<SendOutlined />}
                                          onClick={() => selectedTenantId && handleResendInvite(selectedTenantId, user)}
                                          title={t('admin.userManagement.resendInviteTooltip', { defaultValue: 'Resend invitation email' })}
                                          disabled={isProtectedTarget}
                                        />
                                      )}
                                      {rowEmailEnabled(user) && (
                                        <Button
                                          type="text"
                                          icon={<MailOutlined />}
                                          onClick={() => selectedTenantId && confirmSendResetEmail(selectedTenantId, user)}
                                          title={t('admin.userManagement.sendResetTooltip', { defaultValue: 'Send password reset email' })}
                                          disabled={isProtectedTarget}
                                        />
                                      )}
                                      <Button
                                        type="text"
                                        icon={<KeyOutlined />}
                                        onClick={() => selectedTenantId && openSetPasswordModal(selectedTenantId, user)}
                                        title={t('admin.userManagement.setPasswordTooltip', { defaultValue: 'Set password' })}
                                        disabled={isProtectedTarget}
                                      />
                                      <button
                                        className="btn-icon btn-danger"
                                        onClick={() => openDeleteTenantUserModal(user.user_id, user.display_name || user.email)}
                                        title={user.user_id === currentUserId ? t('admin.userManagement.cannotDeleteSelf', { defaultValue: 'Cannot delete yourself' }) : t('admin.userManagement.deleteUser', { defaultValue: 'Delete user' })}
                                        disabled={user.user_id === currentUserId}
                                      >
                                        <svg viewBox="0 0 24 24" width="16" height="16">
                                          <polyline points="3 6 5 6 21 6" />
                                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                                        </svg>
                                      </button>
                                    </div>
                                  );
                                },
                              },
                            ]}
                            dataSource={sortUsersByRole(tenantUsers)}
                            rowKey="user_id"
                            loading={tenantUsersLoading}
                            pagination={false}
                            className="org-users-table"
                            /* Both axes scroll INSIDE the table box, and the
                               horizontal bar is PINNED to the bottom of the
                               visible area — wide names on a narrow screen
                               must never require scrolling to the page floor
                               to pan sideways.
                               `getContainer` is the load-bearing part: antd
                               anchors its sticky scrollbar to the WINDOW by
                               default, but the thing that actually scrolls
                               here is the surrounding `.card-content`. With
                               the wrong container antd renders no sticky bar
                               at all, which is why a tenant with many users
                               left the scrollbar stranded below the fold. */
                            scroll={{ x: 'max-content', y: 'calc(100vh - 320px)' }}
                            sticky={{
                              offsetScroll: 0,
                              getContainer: orgUsersScrollParent,
                            }}
                            locale={{
                              emptyText: t('admin.noUsersInThisTenant'),
                            }}
                          />
                        </div>
                      )}
                  </div>
                </div>
              </div>
            </div>
            </div>
          )}

          {/* Tenants Tab */}
          {currentTab === 'tenants' && (
            <div className="tab-content active">
              <TenantsTableCard
                tenants={tenants}
                hierarchyTree={hierarchyTree}
                currentUserTenantId={currentUserTenantId ?? undefined}
                loading={hierarchyLoading}
                canCreate={isSuperAdmin || isGroupAdmin}
                onCreate={openCreateTenantModal}
                onToggleStatus={handleToggleTenantStatus}
                onDelete={handleDeleteTenant}
                onRename={isSuperAdmin || isGroupAdmin
                  ? (tenant) => setRenameTarget({ tenant_id: tenant.tenant_id, tenant_name: tenant.tenant_name })
                  : undefined}
              />
            </div>
          )}


          {/* Users Tab */}
          {currentTab === 'users' && (
            <div className="tab-content active">
              <div className="card">
                <div className="card-header">
                  <h3>{t('admin.allUsers')}</h3>
                  {/* Inline Stats */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)', flex: 1, justifyContent: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: 'var(--text-sm)', color: 'var(--claude-text-muted)' }}>
                      <span style={{ fontWeight: 600, color: 'var(--claude-text-primary)' }}>{allUsers.length}</span> users
                    </div>
                    <div style={{ width: '1px', height: '16px', background: 'var(--claude-border)' }} />
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: 'var(--text-sm)', color: 'var(--claude-text-muted)' }}>
                      <span style={{ fontWeight: 600, color: 'var(--claude-text-primary)' }}>{allUsers.filter(u => u.is_active).length}</span> active
                    </div>
                    <div style={{ width: '1px', height: '16px', background: 'var(--claude-border)' }} />
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: 'var(--text-sm)', color: 'var(--claude-text-muted)' }}>
                      <span style={{ fontWeight: 600, color: 'var(--claude-text-primary)' }}>
                        {allUsers.filter(u => {
                          if (!u.last_login) return false;
                          const lastLogin = new Date(u.last_login);
                          const thirtyDaysAgo = new Date();
                          thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
                          return lastLogin >= thirtyDaysAgo;
                        }).length}
                      </span> logged in (30d)
                    </div>
                    <div style={{ width: '1px', height: '16px', background: 'var(--claude-border)' }} />
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: 'var(--text-sm)', color: 'var(--claude-text-muted)' }}>
                      <span style={{ fontWeight: 600, color: 'var(--claude-text-primary)' }}>{new Set(allUsers.map(u => u.tenant_id)).size}</span> tenants
                    </div>
                    <div style={{ width: '1px', height: '16px', background: 'var(--claude-border)' }} />
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: 'var(--text-sm)', color: 'var(--claude-text-muted)' }}>
                      <span style={{ fontWeight: 600, color: 'var(--claude-text-primary)' }}>{allUsers.reduce((sum, u) => sum + (u.total_conversations || 0), 0).toLocaleString()}</span> conversations
                    </div>
                    <div style={{ width: '1px', height: '16px', background: 'var(--claude-border)' }} />
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: 'var(--text-sm)', color: 'var(--claude-text-muted)' }}>
                      <span style={{ fontWeight: 600, color: 'var(--claude-text-primary)' }}>{allUsers.reduce((sum, u) => sum + (u.total_messages || 0), 0).toLocaleString()}</span> messages
                    </div>
                  </div>
                  <button className="btn-primary btn-sm" onClick={() => setShowCreateUserModal(true)}>
                    <svg viewBox="0 0 24 24" width="14" height="14" style={{ fill: 'none', stroke: 'currentColor', strokeWidth: 2 }}>
                      <path d="M12 5v14" />
                      <path d="M5 12h14" />
                    </svg>
                    {t('admin.addUser')}
                  </button>
                </div>

                {/* Column Selector Bar */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  marginBottom: '0',
                  borderBottom: '1px solid var(--claude-border)',
                  background: 'var(--claude-bg-tertiary)',
                  flexShrink: 0
                }}>
                  <Dropdown
                    trigger={['click']}
                    dropdownRender={() => (
                      <div style={{
                        background: 'var(--claude-bg-secondary)',
                        border: '1px solid var(--claude-border)',
                        borderRadius: 'var(--radius)',
                        padding: '8px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.25)'
                      }}>
                        <div style={{ padding: '4px 0' }}>
                          {/* Email/User Column */}
                          <div style={{
                            padding: '4px 8px',
                            cursor: 'pointer',
                            borderRadius: 'var(--radius)',
                            transition: 'background 0.2s',
                            background: 'var(--claude-bg-secondary)'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.background = 'var(--claude-bg-tertiary)'}
                          onMouseLeave={(e) => e.currentTarget.style.background = 'var(--claude-bg-secondary)'}
                          >
                            <Checkbox
                              checked={visibleUsersColumns.includes('email')}
                              onChange={(e) => {
                                e.stopPropagation();
                                if (e.target.checked) {
                                  setVisibleUsersColumns([...visibleUsersColumns, 'email']);
                                } else {
                                  setVisibleUsersColumns(visibleUsersColumns.filter(col => col !== 'email'));
                                }
                              }}
                            >
                              {t('admin.user')}
                            </Checkbox>
                          </div>

                          {/* Tenant Column */}
                          <div style={{
                            padding: '4px 8px',
                            cursor: 'pointer',
                            borderRadius: 'var(--radius)',
                            transition: 'background 0.2s',
                            background: 'var(--claude-bg-secondary)'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.background = 'var(--claude-bg-tertiary)'}
                          onMouseLeave={(e) => e.currentTarget.style.background = 'var(--claude-bg-secondary)'}
                          >
                            <Checkbox
                              checked={visibleUsersColumns.includes('tenant_name')}
                              onChange={(e) => {
                                e.stopPropagation();
                                if (e.target.checked) {
                                  setVisibleUsersColumns([...visibleUsersColumns, 'tenant_name']);
                                } else {
                                  setVisibleUsersColumns(visibleUsersColumns.filter(col => col !== 'tenant_name'));
                                }
                              }}
                            >
                              {t('insights.pulse.tenant')}
                            </Checkbox>
                          </div>

                          {/* Role Column */}
                          <div style={{
                            padding: '4px 8px',
                            cursor: 'pointer',
                            borderRadius: 'var(--radius)',
                            transition: 'background 0.2s',
                            background: 'var(--claude-bg-secondary)'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.background = 'var(--claude-bg-tertiary)'}
                          onMouseLeave={(e) => e.currentTarget.style.background = 'var(--claude-bg-secondary)'}
                          >
                            <Checkbox
                              checked={visibleUsersColumns.includes('role')}
                              onChange={(e) => {
                                e.stopPropagation();
                                if (e.target.checked) {
                                  setVisibleUsersColumns([...visibleUsersColumns, 'role']);
                                } else {
                                  setVisibleUsersColumns(visibleUsersColumns.filter(col => col !== 'role'));
                                }
                              }}
                            >
                              {t('admin.role')}
                            </Checkbox>
                          </div>

                          {/* Tier Column */}
                          <div style={{
                            padding: '4px 8px',
                            cursor: 'pointer',
                            borderRadius: 'var(--radius)',
                            transition: 'background 0.2s',
                            background: 'var(--claude-bg-secondary)'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.background = 'var(--claude-bg-tertiary)'}
                          onMouseLeave={(e) => e.currentTarget.style.background = 'var(--claude-bg-secondary)'}
                          >
                            <Checkbox
                              checked={visibleUsersColumns.includes('user_tier')}
                              onChange={(e) => {
                                e.stopPropagation();
                                if (e.target.checked) {
                                  setVisibleUsersColumns([...visibleUsersColumns, 'user_tier']);
                                } else {
                                  setVisibleUsersColumns(visibleUsersColumns.filter(col => col !== 'user_tier'));
                                }
                              }}
                            >
                              {t('admin.tier')}
                            </Checkbox>
                          </div>

                          {/* Status Column */}
                          <div style={{
                            padding: '4px 8px',
                            cursor: 'pointer',
                            borderRadius: 'var(--radius)',
                            transition: 'background 0.2s',
                            background: 'var(--claude-bg-secondary)'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.background = 'var(--claude-bg-tertiary)'}
                          onMouseLeave={(e) => e.currentTarget.style.background = 'var(--claude-bg-secondary)'}
                          >
                            <Checkbox
                              checked={visibleUsersColumns.includes('is_active')}
                              onChange={(e) => {
                                e.stopPropagation();
                                if (e.target.checked) {
                                  setVisibleUsersColumns([...visibleUsersColumns, 'is_active']);
                                } else {
                                  setVisibleUsersColumns(visibleUsersColumns.filter(col => col !== 'is_active'));
                                }
                              }}
                            >
                              {t('admin.status')}
                            </Checkbox>
                          </div>

                          {/* Total Conversations Column */}
                          <div style={{
                            padding: '4px 8px',
                            cursor: 'pointer',
                            borderRadius: 'var(--radius)',
                            transition: 'background 0.2s',
                            background: 'var(--claude-bg-secondary)'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.background = 'var(--claude-bg-tertiary)'}
                          onMouseLeave={(e) => e.currentTarget.style.background = 'var(--claude-bg-secondary)'}
                          >
                            <Checkbox
                              checked={visibleUsersColumns.includes('total_conversations')}
                              onChange={(e) => {
                                e.stopPropagation();
                                if (e.target.checked) {
                                  setVisibleUsersColumns([...visibleUsersColumns, 'total_conversations']);
                                } else {
                                  setVisibleUsersColumns(visibleUsersColumns.filter(col => col !== 'total_conversations'));
                                }
                              }}
                            >
                              {t('admin.conversations')}
                            </Checkbox>
                          </div>

                          {/* Total Messages Column */}
                          <div style={{
                            padding: '4px 8px',
                            cursor: 'pointer',
                            borderRadius: 'var(--radius)',
                            transition: 'background 0.2s',
                            background: 'var(--claude-bg-secondary)'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.background = 'var(--claude-bg-tertiary)'}
                          onMouseLeave={(e) => e.currentTarget.style.background = 'var(--claude-bg-secondary)'}
                          >
                            <Checkbox
                              checked={visibleUsersColumns.includes('total_messages')}
                              onChange={(e) => {
                                e.stopPropagation();
                                if (e.target.checked) {
                                  setVisibleUsersColumns([...visibleUsersColumns, 'total_messages']);
                                } else {
                                  setVisibleUsersColumns(visibleUsersColumns.filter(col => col !== 'total_messages'));
                                }
                              }}
                            >
                              {t('admin.messages')}
                            </Checkbox>
                          </div>

                          {/* Active Days Column */}
                          <div style={{
                            padding: '4px 8px',
                            cursor: 'pointer',
                            borderRadius: 'var(--radius)',
                            transition: 'background 0.2s',
                            background: 'var(--claude-bg-secondary)'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.background = 'var(--claude-bg-tertiary)'}
                          onMouseLeave={(e) => e.currentTarget.style.background = 'var(--claude-bg-secondary)'}
                          >
                            <Checkbox
                              checked={visibleUsersColumns.includes('active_days')}
                              onChange={(e) => {
                                e.stopPropagation();
                                if (e.target.checked) {
                                  setVisibleUsersColumns([...visibleUsersColumns, 'active_days']);
                                } else {
                                  setVisibleUsersColumns(visibleUsersColumns.filter(col => col !== 'active_days'));
                                }
                              }}
                            >
                              {t('admin.activeDays')}
                            </Checkbox>
                          </div>

                          {/* Created At Column */}
                          <div style={{
                            padding: '4px 8px',
                            cursor: 'pointer',
                            borderRadius: 'var(--radius)',
                            transition: 'background 0.2s',
                            background: 'var(--claude-bg-secondary)'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.background = 'var(--claude-bg-tertiary)'}
                          onMouseLeave={(e) => e.currentTarget.style.background = 'var(--claude-bg-secondary)'}
                          >
                            <Checkbox
                              checked={visibleUsersColumns.includes('created_at')}
                              onChange={(e) => {
                                e.stopPropagation();
                                if (e.target.checked) {
                                  setVisibleUsersColumns([...visibleUsersColumns, 'created_at']);
                                } else {
                                  setVisibleUsersColumns(visibleUsersColumns.filter(col => col !== 'created_at'));
                                }
                              }}
                            >
                              {t('admin.createdAt')}
                            </Checkbox>
                          </div>

                          {/* Last Login Column */}
                          <div style={{
                            padding: '4px 8px',
                            cursor: 'pointer',
                            borderRadius: 'var(--radius)',
                            transition: 'background 0.2s',
                            background: 'var(--claude-bg-secondary)'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.background = 'var(--claude-bg-tertiary)'}
                          onMouseLeave={(e) => e.currentTarget.style.background = 'var(--claude-bg-secondary)'}
                          >
                            <Checkbox
                              checked={visibleUsersColumns.includes('last_login')}
                              onChange={(e) => {
                                e.stopPropagation();
                                if (e.target.checked) {
                                  setVisibleUsersColumns([...visibleUsersColumns, 'last_login']);
                                } else {
                                  setVisibleUsersColumns(visibleUsersColumns.filter(col => col !== 'last_login'));
                                }
                              }}
                            >
                              {t('admin.lastLogin')}
                            </Checkbox>
                          </div>
                        </div>
                      </div>
                    )}
                  >
                    <button className="admin-columns-btn" style={{
                      padding: 'var(--space-2) var(--space-3)',
                      background: 'transparent',
                      border: 'none',
                      color: 'var(--claude-text-secondary)',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'var(--space-1)',
                      fontSize: 'var(--text-sm)',
                      fontWeight: 'var(--font-medium)',
                      transition: 'all 0.2s ease',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'var(--claude-bg-elevated)';
                      e.currentTarget.style.color = 'var(--claude-text-primary)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'transparent';
                      e.currentTarget.style.color = 'var(--claude-text-secondary)';
                    }}
                    >
                      <SettingOutlined style={{ fontSize: '14px' }} />
                      {t('admin.columns')}
                    </button>
                  </Dropdown>
                </div>

                <div className="table-wrapper">
                  <Table<TenantUser>
                    columns={[
                      {
                        title: <div style={{ textAlign: 'center' }}>{t('admin.user')}</div>,
                        dataIndex: 'email',
                        key: 'email',
                        align: 'center' as const,
                        width: usersColumnWidths.email,
                        onHeaderCell: (column: any) => ({
                          width: column.width,
                          onResize: (newWidth: number) => {
                            setUsersColumnWidths(prev => ({ ...prev, email: newWidth }));
                          },
                        }),
                        sorter: (a: TenantUser, b: TenantUser) => a.email.localeCompare(b.email),
                        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: FilterDropdownProps) => {
                          const searchValue = selectedKeys[0] as string || '';
                          const matchType = selectedKeys[1] as string || 'contains';

                          return (
                            <div style={{ padding: 12, width: 280 }}>
                              <Radio.Group
                                value={matchType}
                                onChange={(e) => {
                                  setSelectedKeys([searchValue, e.target.value]);
                                }}
                                style={{ marginBottom: 8, display: 'block' }}
                              >
                                <Radio value="contains">{t('admin.filterContains')}</Radio>
                                <Radio value="exact">{t('admin.filterExactMatch')}</Radio>
                              </Radio.Group>
                              <Input dir="auto"
                                placeholder={t('admin.searchEmailOrName')}
                                value={searchValue}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  setSelectedKeys(val ? [val, matchType] : []);
                                  if (val) {
                                    confirm({ closeDropdown: false });
                                  }
                                }}
                                onPressEnter={() => confirm()}
                                style={{ marginBottom: 8, display: 'block' }}
                              />
                              <Space>
                                <Button
                                  onClick={() => {
                                    clearFilters?.();
                                    confirm();
                                  }}
                                  size="small"
                                >
                                  {t('common.reset')}
                                </Button>
                              </Space>
                            </div>
                          );
                        },
                        filterIcon: (filtered: boolean) => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
                        onFilter: (value: any, record: TenantUser) => {
                          const searchValue = Array.isArray(value) ? value[0] : value;
                          const matchType = Array.isArray(value) ? value[1] : 'contains';
                          const searchTerm = (searchValue as string).toLowerCase();
                          const emailValue = record.email.toLowerCase();
                          const nameValue = (record.display_name || '').toLowerCase();
                          if (matchType === 'exact') {
                            return emailValue === searchTerm || nameValue === searchTerm;
                          }
                          return emailValue.includes(searchTerm) || nameValue.includes(searchTerm);
                        },
                        render: (email: string, user: TenantUser) => (
                          <div className="user-info">
                            <div className="user-avatar">{getInitials((user as any).display_name || (user as any).full_name || email)}</div>
                            <div className="user-details">
                              <div className="user-name" dir="auto">{user.display_name || email}</div>
                              <div className="user-email">{email}</div>
                            </div>
                          </div>
                        ),
                      },
                      {
                        title: <div style={{ textAlign: 'center' }}>{t('admin.tenants')}</div>,
                        dataIndex: 'tenant_name',
                        key: 'tenant_name',
                        align: 'center' as const,
                        width: usersColumnWidths.tenant_name,
                        onHeaderCell: (column: any) => ({
                          width: column.width,
                          onResize: (newWidth: number) => {
                            setUsersColumnWidths(prev => ({ ...prev, tenant_name: newWidth }));
                          },
                        }),
                        sorter: (a: TenantUser, b: TenantUser) => (a.tenant_name || '').localeCompare(b.tenant_name || ''),
                        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: FilterDropdownProps) => (
                          <div style={{ padding: 12, width: 280 }}>
                            <Radio.Group
                              value={selectedKeys[1] || 'contains'}
                              onChange={(e) => {
                                const newKeys = [...selectedKeys];
                                newKeys[1] = e.target.value;
                                setSelectedKeys(newKeys);
                              }}
                              style={{ marginBottom: 8, display: 'block' }}
                            >
                              <Radio value="contains">{t('admin.filterContains')}</Radio>
                              <Radio value="exact">{t('admin.filterExactMatch')}</Radio>
                            </Radio.Group>
                            <Input dir="auto"
                              placeholder={t('admin.searchTenant')}
                              value={selectedKeys[0]}
                              onChange={(e) => {
                                setSelectedKeys(e.target.value ? [e.target.value, selectedKeys[1] || 'contains'] : []);
                                confirm({ closeDropdown: false });
                              }}
                              onPressEnter={() => confirm()}
                              style={{ marginBottom: 8, display: 'block' }}
                            />
                            <Space>
                              <Button onClick={() => clearFilters?.()} size="small">
                                {t('common.reset')}
                              </Button>
                            </Space>
                          </div>
                        ),
                        filterIcon: (filtered: boolean) => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
                        onFilter: (value: any, record: TenantUser) => {
                          const searchValue = Array.isArray(value) ? value[0] : value;
                          const matchType = Array.isArray(value) ? value[1] : 'contains';
                          const searchTerm = (searchValue as string).toLowerCase();
                          const fieldValue = (record.tenant_name || '').toLowerCase();
                          return matchType === 'exact' ? fieldValue === searchTerm : fieldValue.includes(searchTerm);
                        },
                        render: (tenant_name: string) => (
                          <span>{tenant_name || 'Unknown'}</span>
                        ),
                      },
                      {
                        title: <div style={{ textAlign: 'center' }}>{t('admin.role')}</div>,
                        dataIndex: 'role',
                        key: 'role',
                        align: 'center' as const,
                        width: usersColumnWidths.role,
                        onHeaderCell: (column: any) => ({
                          width: column.width,
                          onResize: (newWidth: number) => {
                            setUsersColumnWidths(prev => ({ ...prev, role: newWidth }));
                          },
                        }),
                        sorter: (a: TenantUser, b: TenantUser) => a.role.localeCompare(b.role),
                        filters: [
                          { text: t('admin.user'), value: 'user' },
                          { text: t('nav.admin'), value: 'admin' },
                          { text: t('nav.groupAdmin'), value: 'group_admin' },
                          { text: t('nav.superAdmin'), value: 'super_admin' },
                        ],
                        onFilter: (value: any, record: TenantUser) => record.role === value,
                        render: (role: string, user: TenantUser) => {
                          const roleLabels: Record<string, string> = {
                            user: t('admin.user'),
                            admin: t('nav.admin'),
                            group_admin: t('nav.groupAdmin'),
                            super_admin: t('nav.superAdmin'),
                          };

                          // Build menu items based on what roles can actually be assigned to this user
                          const menuItems: { key: string; label: string }[] = [];

                          // Super admin can assign ANY role to ANYONE - no restrictions
                          if (isSuperAdmin) {
                            menuItems.push({ key: 'user', label: t('admin.user') });
                            menuItems.push({ key: 'admin', label: t('nav.admin') });
                            menuItems.push({ key: 'group_admin', label: t('nav.groupAdmin') });
                            menuItems.push({ key: 'super_admin', label: t('nav.superAdmin') });
                          } else if (isGroupAdmin || isAdmin) {
                            // Non-super admins have limited options
                            menuItems.push({ key: 'user', label: t('admin.user') });
                            menuItems.push({ key: 'admin', label: t('nav.admin') });
                            // group_admin can assign group_admin in own tenant and descendant tenants
                            if (isGroupAdmin) {
                              menuItems.push({ key: 'group_admin', label: t('nav.groupAdmin') });
                            }
                          }

                          return (
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                              <span className={`role-badge role-${role}`}>{roleLabels[role] || role}</span>
                              <Dropdown
                                trigger={['click']}
                                disabled={user.user_id === currentUserId}
                                menu={{
                                  items: menuItems,
                                  onClick: ({ key }) => handleUserRoleChange(user.user_id, key),
                                }}
                              >
                                <Button
                                  type="text"
                                  size="small"
                                  icon={<EditOutlined />}
                                  disabled={user.user_id === currentUserId}
                                  style={{ padding: '2px 4px', height: 'auto' }}
                                />
                              </Dropdown>
                            </div>
                          );
                        },
                      },
                      {
                        title: <div style={{ textAlign: 'center' }}>{t('admin.tier')}</div>,
                        dataIndex: 'user_tier',
                        key: 'user_tier',
                        align: 'center' as const,
                        width: usersColumnWidths.user_tier,
                        onHeaderCell: (column: any) => ({
                          width: column.width,
                          onResize: (newWidth: number) => {
                            setUsersColumnWidths(prev => ({ ...prev, user_tier: newWidth }));
                          },
                        }),
                        sorter: (a: TenantUser, b: TenantUser) => (a.user_tier || 1) - (b.user_tier || 1),
                        render: (tier: number, user: TenantUser) => (
                          <Select
                            size="small"
                            value={tier || 1}
                            onChange={(value: number) => handleUserTierChange(user.user_id, value)}
                            style={{ width: 85 }}
                          >
                            <Select.Option value={1}>Tier 1</Select.Option>
                            <Select.Option value={2}>Tier 2</Select.Option>
                            <Select.Option value={3}>Tier 3</Select.Option>
                            <Select.Option value={4}>Tier 4</Select.Option>
                            <Select.Option value={5}>Tier 5</Select.Option>
                          </Select>
                        ),
                      },
                      {
                        title: <div style={{ textAlign: 'center' }}>{t('admin.status')}</div>,
                        dataIndex: 'is_active',
                        key: 'is_active',
                        align: 'center' as const,
                        width: usersColumnWidths.is_active,
                        onHeaderCell: (column: any) => ({
                          width: column.width,
                          onResize: (newWidth: number) => {
                            setUsersColumnWidths(prev => ({ ...prev, is_active: newWidth }));
                          },
                        }),
                        sorter: (a: TenantUser, b: TenantUser) => Number(b.is_active) - Number(a.is_active),
                        filters: [
                          { text: t('admin.active'), value: true },
                          { text: t('admin.inactive'), value: false },
                        ],
                        onFilter: (value: any, record: TenantUser) => record.is_active === value,
                        render: (is_active: boolean, user: TenantUser) => (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                            <label className={`status-switch ${user.user_id === currentUserId ? 'disabled' : ''}`}>
                              <input
                                type="checkbox"
                                checked={is_active}
                                onChange={() => handleToggleUserStatus(user.user_id)}
                                disabled={user.user_id === currentUserId}
                              />
                              <span className="switch-slider"></span>
                              <span className="switch-label">{is_active ? t('common.active') : t('common.inactive')}</span>
                            </label>
                            {user.has_password === false && (
                              <Tag color="gold" title={t('admin.userManagement.pendingTooltip', { defaultValue: "Invited — hasn't set a password yet" })}>{t('admin.userManagement.pending', { defaultValue: 'Pending' })}</Tag>
                            )}
                          </span>
                        ),
                      },
                      {
                        title: <div style={{ textAlign: 'center' }}>{t('admin.conversations')}</div>,
                        dataIndex: 'total_conversations',
                        key: 'total_conversations',
                        align: 'center' as const,
                        width: usersColumnWidths.total_conversations,
                        onHeaderCell: (column: any) => ({
                          width: column.width,
                          onResize: (newWidth: number) => {
                            setUsersColumnWidths(prev => ({ ...prev, total_conversations: newWidth }));
                          },
                        }),
                        sorter: (a: TenantUser, b: TenantUser) => (a.total_conversations || 0) - (b.total_conversations || 0),
                        filterDropdown: createNumericFilterDropdown,
                        filterIcon: (filtered: boolean) => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
                        onFilter: createNumericOnFilter((record: TenantUser) => record.total_conversations || 0),
                        render: (total_conversations: number) => (
                          <span>{total_conversations || 0}</span>
                        ),
                      },
                      {
                        title: <div style={{ textAlign: 'center' }}>{t('admin.messages')}</div>,
                        dataIndex: 'total_messages',
                        key: 'total_messages',
                        align: 'center' as const,
                        width: usersColumnWidths.total_messages,
                        onHeaderCell: (column: any) => ({
                          width: column.width,
                          onResize: (newWidth: number) => {
                            setUsersColumnWidths(prev => ({ ...prev, total_messages: newWidth }));
                          },
                        }),
                        sorter: (a: TenantUser, b: TenantUser) => (a.total_messages || 0) - (b.total_messages || 0),
                        filterDropdown: createNumericFilterDropdown,
                        filterIcon: (filtered: boolean) => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
                        onFilter: createNumericOnFilter((record: TenantUser) => record.total_messages || 0),
                        render: (total_messages: number) => (
                          <span>{total_messages || 0}</span>
                        ),
                      },
                      {
                        title: <div style={{ textAlign: 'center' }}>{t('admin.activeDays')}</div>,
                        dataIndex: 'active_days',
                        key: 'active_days',
                        align: 'center' as const,
                        width: usersColumnWidths.active_days,
                        onHeaderCell: (column: any) => ({
                          width: column.width,
                          onResize: (newWidth: number) => {
                            setUsersColumnWidths(prev => ({ ...prev, active_days: newWidth }));
                          },
                        }),
                        sorter: (a: TenantUser, b: TenantUser) => (a.active_days || 0) - (b.active_days || 0),
                        filterDropdown: createNumericFilterDropdown,
                        filterIcon: (filtered: boolean) => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
                        onFilter: createNumericOnFilter((record: TenantUser) => record.active_days || 0),
                        render: (active_days: number) => (
                          <span>{active_days || 0}</span>
                        ),
                      },
                      {
                        title: <div style={{ textAlign: 'center' }}>{t('admin.createdAt')}</div>,
                        dataIndex: 'created_at',
                        key: 'created_at',
                        align: 'center' as const,
                        width: usersColumnWidths.created_at,
                        onHeaderCell: (column: any) => ({
                          width: column.width,
                          onResize: (newWidth: number) => {
                            setUsersColumnWidths(prev => ({ ...prev, created_at: newWidth }));
                          },
                        }),
                        sorter: (a: TenantUser, b: TenantUser) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
                        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: FilterDropdownProps) => {
                          // Parse date range from stored JSON string
                          let dateRange: [Dayjs, Dayjs] | null = null;
                          if (selectedKeys[0]) {
                            try {
                              const parsed = JSON.parse(selectedKeys[0] as string);
                              if (parsed && parsed.length === 2) {
                                dateRange = [dayjs(parsed[0]), dayjs(parsed[1])];
                              }
                            } catch {}
                          }

                          return (
                            <div style={{ padding: 12 }}>
                              <DatePicker.RangePicker
                                value={dateRange}
                                onChange={(dates) => {
                                  if (dates && dates[0] && dates[1]) {
                                    // Store as JSON string
                                    const jsonString = JSON.stringify([dates[0].toISOString(), dates[1].toISOString()]);
                                    setSelectedKeys([jsonString]);
                                  } else {
                                    setSelectedKeys([]);
                                  }
                                }}
                                style={{ marginBottom: 8, display: 'block' }}
                              />
                              <Space>
                                <Button
                                  onClick={() => confirm()}
                                  size="small"
                                  type="primary"
                                  disabled={!dateRange}
                                >
                                  {t('common.filter')}
                                </Button>
                                <Button
                                  onClick={() => {
                                    clearFilters?.();
                                    confirm();
                                  }}
                                  size="small"
                                >
                                  {t('common.reset')}
                                </Button>
                              </Space>
                            </div>
                          );
                        },
                        filterIcon: (filtered: boolean) => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
                        onFilter: (value: any, record: TenantUser) => {
                          if (!value) return true;
                          try {
                            const parsed = JSON.parse(value as string);
                            if (!parsed || !Array.isArray(parsed) || parsed.length !== 2) return true;

                            const start = dayjs(parsed[0]);
                            const end = dayjs(parsed[1]);
                            const recordDate = dayjs(record.created_at);
                            const startOfDay = start.startOf('day');
                            const endOfDay = end.endOf('day');

                            return (
                              (recordDate.isAfter(startOfDay) || recordDate.isSame(startOfDay)) &&
                              (recordDate.isBefore(endOfDay) || recordDate.isSame(endOfDay))
                            );
                          } catch {
                            return true;
                          }
                        },
                        render: (created_at: string) => {
                          const date = new Date(created_at);
                          return <span>{date.toLocaleDateString(activeLocale(), { year: 'numeric', month: 'short', day: 'numeric' })}</span>;
                        },
                      },
                      {
                        title: <div style={{ textAlign: 'center' }}>{t('admin.lastLogin')}</div>,
                        dataIndex: 'last_login',
                        key: 'last_login',
                        align: 'center' as const,
                        width: usersColumnWidths.last_login,
                        onHeaderCell: (column: any) => ({
                          width: column.width,
                          onResize: (newWidth: number) => {
                            setUsersColumnWidths(prev => ({ ...prev, last_login: newWidth }));
                          },
                        }),
                        sorter: (a: TenantUser, b: TenantUser) => {
                          const dateA = a.last_login ? new Date(a.last_login).getTime() : 0;
                          const dateB = b.last_login ? new Date(b.last_login).getTime() : 0;
                          return dateA - dateB;
                        },
                        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: FilterDropdownProps) => {
                          // Parse date range from stored JSON string
                          let dateRange: [Dayjs, Dayjs] | null = null;
                          if (selectedKeys[0]) {
                            try {
                              const parsed = JSON.parse(selectedKeys[0] as string);
                              if (parsed && parsed.length === 2) {
                                dateRange = [dayjs(parsed[0]), dayjs(parsed[1])];
                              }
                            } catch {}
                          }

                          return (
                            <div style={{ padding: 12 }}>
                              <DatePicker.RangePicker
                                value={dateRange}
                                onChange={(dates) => {
                                  if (dates && dates[0] && dates[1]) {
                                    // Store as JSON string
                                    const jsonString = JSON.stringify([dates[0].toISOString(), dates[1].toISOString()]);
                                    setSelectedKeys([jsonString]);
                                  } else {
                                    setSelectedKeys([]);
                                  }
                                }}
                                style={{ marginBottom: 8, display: 'block' }}
                              />
                              <Space>
                                <Button
                                  onClick={() => confirm()}
                                  size="small"
                                  type="primary"
                                  disabled={!dateRange}
                                >
                                  {t('common.filter')}
                                </Button>
                                <Button
                                  onClick={() => {
                                    clearFilters?.();
                                    confirm();
                                  }}
                                  size="small"
                                >
                                  {t('common.reset')}
                                </Button>
                              </Space>
                            </div>
                          );
                        },
                        filterIcon: (filtered: boolean) => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
                        onFilter: (value: any, record: TenantUser) => {
                          // If no filter is set, show all records
                          if (!value) return true;

                          // If user never logged in, exclude them when date filter is active
                          if (!record.last_login) return false;

                          try {
                            const parsed = JSON.parse(value as string);
                            if (!parsed || !Array.isArray(parsed) || parsed.length !== 2) return true;

                            const start = dayjs(parsed[0]);
                            const end = dayjs(parsed[1]);
                            const recordDate = dayjs(record.last_login);
                            const startOfDay = start.startOf('day');
                            const endOfDay = end.endOf('day');

                            return (
                              (recordDate.isAfter(startOfDay) || recordDate.isSame(startOfDay)) &&
                              (recordDate.isBefore(endOfDay) || recordDate.isSame(endOfDay))
                            );
                          } catch {
                            return true;
                          }
                        },
                        render: (last_login: string | null) => {
                          if (!last_login) return <span style={{ color: 'var(--claude-text-muted)' }}>{t('admin.never')}</span>;
                          const date = new Date(last_login);
                          return <span>{date.toLocaleDateString(activeLocale(), { year: 'numeric', month: 'short', day: 'numeric' })}</span>;
                        },
                      },
                      {
                        title: <div style={{ textAlign: 'center' }}>{t('admin.actions')}</div>,
                        key: 'actions',
                        align: 'center' as const,
                        width: usersColumnWidths.actions,
                        render: (_: any, user: TenantUser) => {
                          const isProtectedTarget = user.role === 'super_admin' && !isSuperAdmin;
                          return (
                            <Space size={0}>
                              {user.has_password === false && canInvite && rowEmailEnabled(user) && (
                                <Button
                                  type="text"
                                  icon={<SendOutlined />}
                                  onClick={() => handleResendInvite(user.tenant_id, user)}
                                  title={t('admin.userManagement.resendInviteTooltip', { defaultValue: 'Resend invitation email' })}
                                  disabled={isProtectedTarget}
                                />
                              )}
                              {rowEmailEnabled(user) && (
                                <Button
                                  type="text"
                                  icon={<MailOutlined />}
                                  onClick={() => confirmSendResetEmail(user.tenant_id, user)}
                                  title={t('admin.userManagement.sendResetTooltip', { defaultValue: 'Send password reset email' })}
                                  disabled={isProtectedTarget}
                                />
                              )}
                              <Button
                                type="text"
                                icon={<KeyOutlined />}
                                onClick={() => openSetPasswordModal(user.tenant_id, user)}
                                title={t('admin.userManagement.setPasswordTooltip', { defaultValue: 'Set password' })}
                                disabled={isProtectedTarget}
                              />
                              <Button
                                type="text"
                                danger
                                icon={<DeleteOutlined />}
                                onClick={() => openDeleteUserModal(user.user_id, user.display_name || user.email)}
                                title={user.user_id === currentUserId ? t('admin.userManagement.cannotDeleteSelf', { defaultValue: 'Cannot delete yourself' }) : t('admin.userManagement.deleteUser', { defaultValue: 'Delete user' })}
                                disabled={user.user_id === currentUserId}
                              />
                            </Space>
                          );
                        },
                      },
                    ].filter(col => {
                      if (col.key === 'actions') return true;
                      return visibleUsersColumns.includes(col.key as string);
                    })}
                    components={{
                      header: {
                        cell: ResizableTitle,
                      },
                    }}
                    dataSource={allUsers}
                    rowKey="user_id"
                    loading={usersLoading}
                    pagination={false}
                    scroll={{ y: 'calc(100vh - 320px)' }}
                    locale={{
                      emptyText: t('admin.noUsersFound'),
                    }}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Agents Tab */}
          {currentTab === 'bots' && (
            <div className="tab-content active">
              <div className="card">
                <div className="card-header">
                  <h3>{t('admin.activeAgents')}</h3>
                  {/* Inline Stats */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)', flex: 1, justifyContent: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: 'var(--text-sm)', color: 'var(--claude-text-muted)' }}>
                      <span style={{ fontWeight: 600, color: 'var(--claude-text-primary)' }}>{allBots.length}</span> agents
                    </div>
                    <div style={{ width: '1px', height: '16px', background: 'var(--claude-border)' }} />
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: 'var(--text-sm)', color: 'var(--claude-text-muted)' }}>
                      <span style={{ fontWeight: 600, color: 'var(--claude-text-primary)' }}>{allBots.reduce((sum, b) => sum + b.total_conversations, 0).toLocaleString()}</span> conversations
                    </div>
                    <div style={{ width: '1px', height: '16px', background: 'var(--claude-border)' }} />
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: 'var(--text-sm)', color: 'var(--claude-text-muted)' }}>
                      <span style={{ fontWeight: 600, color: 'var(--claude-text-primary)' }}>{allBots.reduce((sum, b) => sum + b.total_messages, 0).toLocaleString()}</span> messages
                    </div>
                  </div>
                </div>

                {/* Column Selector Bar */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  marginBottom: '0',
                  borderBottom: '1px solid var(--claude-border)',
                  background: 'var(--claude-bg-tertiary)',
                  flexShrink: 0
                }}>
                  <Dropdown
                    trigger={['click']}
                    dropdownRender={() => (
                      <div style={{
                        background: 'var(--claude-bg-secondary)',
                        border: '1px solid var(--claude-border)',
                        borderRadius: 'var(--radius)',
                        padding: '8px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.25)'
                      }}>
                        <div style={{ padding: '4px 0' }}>
                          {[
                            { key: 'chatbot_name', label: t('agents.agentName') },
                            { key: 'creator_email', label: t('admin.creator') },
                            { key: 'sharing', label: t('admin.sharing') },
                            { key: 'total_conversations', label: t('admin.conversations') },
                            { key: 'total_messages', label: t('admin.messages') },
                            { key: 'total_cost_usd', label: t('admin.totalCost') },
                            { key: 'unique_users', label: t('admin.uniqueUsers') },
                            { key: 'actions', label: t('admin.actions') },
                          ].map(col => (
                            <div key={col.key} style={{
                              padding: '4px 8px',
                              cursor: 'pointer',
                              borderRadius: 'var(--radius)',
                              transition: 'background 0.2s',
                              background: 'var(--claude-bg-secondary)'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.background = 'var(--claude-bg-tertiary)'}
                            onMouseLeave={(e) => e.currentTarget.style.background = 'var(--claude-bg-secondary)'}
                            >
                              <Checkbox
                                checked={visibleBotsColumns.includes(col.key)}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  if (e.target.checked) {
                                    setVisibleBotsColumns([...visibleBotsColumns, col.key]);
                                  } else {
                                    setVisibleBotsColumns(visibleBotsColumns.filter(c => c !== col.key));
                                  }
                                }}
                              >
                                {col.label}
                              </Checkbox>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  >
                    <button className="admin-columns-btn" style={{
                      padding: 'var(--space-2) var(--space-3)',
                      background: 'transparent',
                      border: 'none',
                      color: 'var(--claude-text-secondary)',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'var(--space-1)',
                      fontSize: 'var(--text-sm)'
                    }}>
                      <SettingOutlined />
                      {t('admin.columns')}
                    </button>
                  </Dropdown>
                </div>

                <div className="table-wrapper">
                  <Table<BotAnalytics>
                    dataSource={allBots}
                    columns={[
                      {
                        title: <div style={{ textAlign: 'center' }}>{t('admin.agentName')}</div>,
                        dataIndex: 'chatbot_name',
                        key: 'chatbot_name',
                        width: botsColumnWidths.chatbot_name,
                        onHeaderCell: (column: any) => ({
                          width: column.width,
                          onResize: (newWidth: number) => {
                            setBotsColumnWidths(prev => ({ ...prev, chatbot_name: newWidth }));
                          },
                        }),
                        sorter: (a: BotAnalytics, b: BotAnalytics) => a.chatbot_name.localeCompare(b.chatbot_name),
                        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: FilterDropdownProps) => {
                          const searchValue = selectedKeys[0] as string || '';
                          const matchType = selectedKeys[1] as string || 'contains';

                          return (
                            <div style={{ padding: 12, width: 280 }}>
                              <Radio.Group
                                value={matchType}
                                onChange={(e) => {
                                  setSelectedKeys([searchValue, e.target.value]);
                                }}
                                style={{ marginBottom: 8, display: 'block' }}
                              >
                                <Radio value="contains">{t('admin.filterContains')}</Radio>
                                <Radio value="exact">{t('admin.filterExactMatch')}</Radio>
                              </Radio.Group>
                              <Input dir="auto"
                                placeholder={t('admin.searchAgentName')}
                                value={searchValue}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  setSelectedKeys(val ? [val, matchType] : []);
                                  if (val) {
                                    confirm({ closeDropdown: false });
                                  }
                                }}
                                onPressEnter={() => confirm()}
                                style={{ marginBottom: 8, display: 'block' }}
                              />
                              <Space>
                                <Button
                                  onClick={() => {
                                    clearFilters?.();
                                    confirm();
                                  }}
                                  size="small"
                                >
                                  {t('common.reset')}
                                </Button>
                              </Space>
                            </div>
                          );
                        },
                        filterIcon: (filtered: boolean) => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
                        onFilter: (value: any, record: BotAnalytics) => {
                          const searchValue = Array.isArray(value) ? value[0] : value;
                          const matchType = Array.isArray(value) ? value[1] : 'contains';
                          const searchTerm = (searchValue as string).toLowerCase();
                          const fieldValue = record.chatbot_name.toLowerCase();
                          return matchType === 'exact' ? fieldValue === searchTerm : fieldValue.includes(searchTerm);
                        },
                        render: (name: string, bot: BotAnalytics) => (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <div style={{
                              width: 28,
                              height: 28,
                              borderRadius: '6px',
                              background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.15), rgba(59, 130, 246, 0.05))',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0,
                              border: '1px solid rgba(59, 130, 246, 0.2)',
                            }}>
                              <svg
                                viewBox="0 0 24 24"
                                width="14"
                                height="14"
                                style={{
                                  stroke: '#3b82f6',
                                  fill: 'none',
                                  strokeWidth: 2,
                                  strokeLinecap: 'round',
                                  strokeLinejoin: 'round'
                                }}
                              >
                                <rect x="3" y="3" width="7" height="7" />
                                <rect x="14" y="3" width="7" height="7" />
                                <rect x="14" y="14" width="7" height="7" />
                                <rect x="3" y="14" width="7" height="7" />
                              </svg>
                            </div>
                            <div style={{ minWidth: 0 }}>
                              <div style={{ fontWeight: 500, color: 'var(--claude-text-primary)' }}>{name}</div>
                              {bot.chatbot_description && (
                                <div dir="auto" style={{
                                  fontSize: '11px',
                                  color: 'var(--claude-text-muted)',
                                  marginTop: '1px',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  maxWidth: '200px'
                                }}>
                                  {bot.chatbot_description}
                                </div>
                              )}
                            </div>
                          </div>
                        ),
                      },
                      {
                        title: <div style={{ textAlign: 'center' }}>{t('admin.creator')}</div>,
                        dataIndex: 'creator_email',
                        key: 'creator_email',
                        width: botsColumnWidths.creator_email,
                        onHeaderCell: (column: any) => ({
                          width: column.width,
                          onResize: (newWidth: number) => {
                            setBotsColumnWidths(prev => ({ ...prev, creator_email: newWidth }));
                          },
                        }),
                        sorter: (a: BotAnalytics, b: BotAnalytics) => a.creator_email.localeCompare(b.creator_email),
                        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: FilterDropdownProps) => {
                          const searchValue = selectedKeys[0] as string || '';
                          const matchType = selectedKeys[1] as string || 'contains';

                          return (
                            <div style={{ padding: 12, width: 280 }}>
                              <Radio.Group
                                value={matchType}
                                onChange={(e) => {
                                  setSelectedKeys([searchValue, e.target.value]);
                                }}
                                style={{ marginBottom: 8, display: 'block' }}
                              >
                                <Radio value="contains">{t('admin.filterContains')}</Radio>
                                <Radio value="exact">{t('admin.filterExactMatch')}</Radio>
                              </Radio.Group>
                              <Input dir="auto"
                                placeholder={t('admin.searchCreator')}
                                value={searchValue}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  setSelectedKeys(val ? [val, matchType] : []);
                                  if (val) {
                                    confirm({ closeDropdown: false });
                                  }
                                }}
                                onPressEnter={() => confirm()}
                                style={{ marginBottom: 8, display: 'block' }}
                              />
                              <Space>
                                <Button
                                  onClick={() => {
                                    clearFilters?.();
                                    confirm();
                                  }}
                                  size="small"
                                >
                                  {t('common.reset')}
                                </Button>
                              </Space>
                            </div>
                          );
                        },
                        filterIcon: (filtered: boolean) => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
                        onFilter: (value: any, record: BotAnalytics) => {
                          const searchValue = Array.isArray(value) ? value[0] : value;
                          const matchType = Array.isArray(value) ? value[1] : 'contains';
                          const searchTerm = (searchValue as string).toLowerCase();
                          const emailValue = record.creator_email.toLowerCase();
                          const nameValue = (record.creator_name || '').toLowerCase();
                          if (matchType === 'exact') {
                            return emailValue === searchTerm || nameValue === searchTerm;
                          }
                          return emailValue.includes(searchTerm) || nameValue.includes(searchTerm);
                        },
                        render: (email: string, bot: BotAnalytics) => (
                          <div>
                            <div>{email}</div>
                            {bot.creator_name && (
                              <div style={{ fontSize: '12px', color: 'var(--claude-text-secondary)' }} dir="auto">
                                {bot.creator_name}
                              </div>
                            )}
                          </div>
                        ),
                      },
                      {
                        title: <div style={{ textAlign: 'center' }}>{t('admin.sharing')}</div>,
                        key: 'sharing',
                        align: 'center' as const,
                        width: botsColumnWidths.sharing,
                        onHeaderCell: (column: any) => ({
                          width: column.width,
                          onResize: (newWidth: number) => {
                            setBotsColumnWidths(prev => ({ ...prev, sharing: newWidth }));
                          },
                        }),
                        sorter: (a: BotAnalytics, b: BotAnalytics) =>
                          (a.shared_tenant_count + a.shared_user_count) - (b.shared_tenant_count + b.shared_user_count),
                        render: (_: unknown, bot: BotAnalytics) => {
                          const totalShares = bot.shared_tenant_count + bot.shared_user_count;
                          if (totalShares === 0) {
                            return <span style={{ color: 'var(--claude-text-muted)' }}>--</span>;
                          }
                          const details = bot.share_details || { tenants: [], users: [] };
                          const term = shareSearchTerm.toLowerCase();
                          const filteredTenants = term
                            ? details.tenants.filter(t => t.tenant_name.toLowerCase().includes(term))
                            : details.tenants;
                          const filteredUsers = term
                            ? details.users.filter(u =>
                                u.email.toLowerCase().includes(term) ||
                                (u.name || '').toLowerCase().includes(term) ||
                                u.tenant_name.toLowerCase().includes(term))
                            : details.users;
                          return (
                            <Popover
                              trigger="click"
                              placement={mirrorPlacement(direction, 'bottomLeft')}
                              overlayStyle={{ maxWidth: 400 }}
                              onOpenChange={(open) => { if (!open) setShareSearchTerm(''); }}
                              content={
                                <div style={{ minWidth: 280 }}>
                                  <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 8,
                                    padding: '6px 10px',
                                    background: 'var(--claude-bg-tertiary)',
                                    border: '1px solid var(--claude-border)',
                                    borderRadius: 'var(--radius)',
                                    marginBottom: 10,
                                    transition: 'border-color 0.2s',
                                  }}>
                                    <SearchOutlined style={{ color: 'var(--claude-text-muted)', fontSize: 13, flexShrink: 0 }} />
                                    <input dir="auto"
                                      placeholder={t('admin.searchPlaceholder')}
                                      value={shareSearchTerm}
                                      onChange={(e) => setShareSearchTerm(e.target.value)}
                                      autoFocus
                                      style={{
                                        border: 'none',
                                        outline: 'none',
                                        background: 'transparent',
                                        color: 'var(--claude-text-primary)',
                                        fontSize: 'var(--text-sm)',
                                        width: '100%',
                                        padding: 0,
                                      }}
                                    />
                                    {shareSearchTerm && (
                                      <button
                                        onClick={() => setShareSearchTerm('')}
                                        style={{
                                          border: 'none',
                                          background: 'none',
                                          color: 'var(--claude-text-muted)',
                                          cursor: 'pointer',
                                          padding: 0,
                                          fontSize: 12,
                                          lineHeight: 1,
                                          flexShrink: 0,
                                        }}
                                      >
                                        &times;
                                      </button>
                                    )}
                                  </div>
                                  <div style={{ maxHeight: 300, overflowY: 'auto' }}>
                                    {filteredTenants.length > 0 && (
                                      <div style={{ marginBottom: filteredUsers.length > 0 ? 12 : 0 }}>
                                        <div style={{
                                          fontSize: 11,
                                          fontWeight: 600,
                                          textTransform: 'uppercase',
                                          color: 'var(--claude-text-muted)',
                                          marginBottom: 6,
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: 4
                                        }}>
                                          <ApartmentOutlined /> Tenants ({filteredTenants.length})
                                        </div>
                                        {filteredTenants.map(t => (
                                          <div key={t.tenant_id} style={{
                                            padding: '4px 0',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            gap: 8,
                                            borderBottom: '1px solid var(--claude-border)',
                                          }}>
                                            <span style={{ fontWeight: 500, color: 'var(--claude-text-primary)' }} dir="auto">{t.tenant_name}</span>
                                            <div style={{ display: 'flex', gap: 4 }}>
                                              <Tag color="blue" style={{ margin: 0, fontSize: 10 }}>{t.permission_level}</Tag>
                                              {t.can_reshare && <Tag color="green" style={{ margin: 0, fontSize: 10 }}>reshare</Tag>}
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                    {filteredUsers.length > 0 && (
                                      <div>
                                        <div style={{
                                          fontSize: 11,
                                          fontWeight: 600,
                                          textTransform: 'uppercase',
                                          color: 'var(--claude-text-muted)',
                                          marginBottom: 6,
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: 4
                                        }}>
                                          <TeamOutlined /> Users ({filteredUsers.length})
                                        </div>
                                        {filteredUsers.map(u => (
                                          <div key={u.user_id} style={{
                                            padding: '4px 0',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            gap: 8,
                                            borderBottom: '1px solid var(--claude-border)',
                                          }}>
                                            <div style={{ minWidth: 0 }}>
                                              <div style={{ fontWeight: 500, color: 'var(--claude-text-primary)', fontSize: 13 }}>{u.email}</div>
                                              {u.name && <div style={{ fontSize: 11, color: 'var(--claude-text-muted)' }} dir="auto">{u.name}</div>}
                                              <div style={{ fontSize: 11, color: 'var(--claude-text-secondary)' }} dir="auto">{u.tenant_name}</div>
                                            </div>
                                            <Tag color="blue" style={{ margin: 0, fontSize: 10, flexShrink: 0 }}>{u.permission_level}</Tag>
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                    {filteredTenants.length === 0 && filteredUsers.length === 0 && term && (
                                      <div style={{ textAlign: 'center', padding: 12, color: 'var(--claude-text-muted)', fontSize: 13 }}>
                                        {t('admin.noMatchesFound')}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              }
                              title={
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                  <ShareAltOutlined />
                                  <span>{t('admin.sharedWith')}</span>
                                </div>
                              }
                            >
                              <Button
                                type="text"
                                size="small"
                                icon={<ShareAltOutlined />}
                                style={{ color: 'var(--claude-accent)' }}
                              >
                                {totalShares}
                              </Button>
                            </Popover>
                          );
                        },
                      },
                      {
                        title: <div style={{ textAlign: 'center' }}>{t('admin.conversations')}</div>,
                        dataIndex: 'total_conversations',
                        key: 'total_conversations',
                        align: 'center' as const,
                        width: botsColumnWidths.total_conversations,
                        onHeaderCell: (column: any) => ({
                          width: column.width,
                          onResize: (newWidth: number) => {
                            setBotsColumnWidths(prev => ({ ...prev, total_conversations: newWidth }));
                          },
                        }),
                        sorter: (a: BotAnalytics, b: BotAnalytics) => a.total_conversations - b.total_conversations,
                        filterDropdown: createNumericFilterDropdown,
                        filterIcon: (filtered: boolean) => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
                        onFilter: createNumericOnFilter((record: BotAnalytics) => record.total_conversations),
                        render: (count: number) => <span>{count}</span>,
                      },
                      {
                        title: <div style={{ textAlign: 'center' }}>{t('admin.messages')}</div>,
                        dataIndex: 'total_messages',
                        key: 'total_messages',
                        align: 'center' as const,
                        width: botsColumnWidths.total_messages,
                        onHeaderCell: (column: any) => ({
                          width: column.width,
                          onResize: (newWidth: number) => {
                            setBotsColumnWidths(prev => ({ ...prev, total_messages: newWidth }));
                          },
                        }),
                        sorter: (a: BotAnalytics, b: BotAnalytics) => a.total_messages - b.total_messages,
                        filterDropdown: createNumericFilterDropdown,
                        filterIcon: (filtered: boolean) => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
                        onFilter: createNumericOnFilter((record: BotAnalytics) => record.total_messages),
                        render: (count: number) => <span>{count.toLocaleString()}</span>,
                      },
                      {
                        title: <div style={{ textAlign: 'center' }}>{t('admin.totalCost')}</div>,
                        dataIndex: 'total_cost_usd',
                        key: 'total_cost_usd',
                        align: 'center' as const,
                        width: botsColumnWidths.total_cost_usd,
                        onHeaderCell: (column: any) => ({
                          width: column.width,
                          onResize: (newWidth: number) => {
                            setBotsColumnWidths(prev => ({ ...prev, total_cost_usd: newWidth }));
                          },
                        }),
                        sorter: (a: BotAnalytics, b: BotAnalytics) => a.total_cost_usd - b.total_cost_usd,
                        filterDropdown: createNumericFilterDropdown,
                        filterIcon: (filtered: boolean) => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
                        onFilter: createNumericOnFilter((record: BotAnalytics) => record.total_cost_usd),
                        render: (cost: number) => {
                          const costVal = cost || 0;
                          return <span style={{ color: costVal > 0 ? 'var(--claude-text-primary)' : 'var(--claude-text-muted)' }}>${costVal.toFixed(2)}</span>;
                        },
                      },
                      {
                        title: <div style={{ textAlign: 'center' }}>{t('admin.uniqueUsers')}</div>,
                        dataIndex: 'unique_users',
                        key: 'unique_users',
                        align: 'center' as const,
                        width: botsColumnWidths.unique_users,
                        onHeaderCell: (column: any) => ({
                          width: column.width,
                          onResize: (newWidth: number) => {
                            setBotsColumnWidths(prev => ({ ...prev, unique_users: newWidth }));
                          },
                        }),
                        sorter: (a: BotAnalytics, b: BotAnalytics) => a.unique_users - b.unique_users,
                        filterDropdown: createNumericFilterDropdown,
                        filterIcon: (filtered: boolean) => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
                        onFilter: createNumericOnFilter((record: BotAnalytics) => record.unique_users),
                        render: (count: number) => <span>{count}</span>,
                      },
                      {
                        title: t('admin.actions'),
                        key: 'actions',
                        width: botsColumnWidths.actions,
                        align: 'center' as const,
                        onHeaderCell: (column: any) => ({
                          width: column.width,
                          onResize: (newWidth: number) => {
                            setBotsColumnWidths(prev => ({ ...prev, actions: newWidth }));
                          },
                        }),
                        render: (_: unknown, bot: BotAnalytics) => (
                          <Button
                            type="text"
                            danger
                            icon={<DeleteOutlined />}
                            onClick={() => handleDeleteBot(bot.chatbot_id, bot.chatbot_name)}
                            title={t('admin.deleteAgent')}
                          />
                        ),
                      },
                    ].filter(col => {
                      if (col.key === 'actions') return true; // Always show actions
                      return visibleBotsColumns.includes(col.key as string);
                    })}
                    components={{
                      header: {
                        cell: ResizableTitle,
                      },
                    }}
                    rowKey="chatbot_id"
                    loading={botsLoading}
                    pagination={false}
                    scroll={{ y: 'calc(100vh - 320px)' }}
                    locale={{
                      emptyText: t('admin.noAgentsFound'),
                    }}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Settings Tab */}
          {currentTab === 'settings' && (
            <div className="tab-content active">
              <div className="tenant-prompt-section">
                <div className="tenant-prompt-header">
                  <div>
                    <h3 className="tenant-prompt-title">{t('admin.organizationPrompt')}</h3>
                    <p className="text-muted">{t('admin.orgPromptSubtitle')}</p>
                  </div>
                  <button
                    className="btn-primary"
                    onClick={handleSaveTenantPrompt}
                    disabled={settingsLoading}
                  >
                    {settingsLoading ? t('common.saving') : t('admin.saveChanges')}
                  </button>
                </div>

                <div className="form-group">
                  <label htmlFor="tenantPromptTextarea" className="form-label">
                    {t('admin.tenantPrompt')}
                  </label>
                  <textarea dir="auto"
                    id="tenantPromptTextarea"
                    className="form-textarea tenant-prompt-textarea"
                    placeholder={t('admin.tenantPromptPlaceholder')}
                    value={tenantPrompt}
                    onChange={(e) => setTenantPrompt(e.target.value)}
                    rows={10}
                  />
                  <div className="form-help">{t('admin.orgPromptHelp')}</div>
                </div>

                {/* Prompt Strategy Explanation */}
                <div className="prompt-strategy-info">
                  <h4>{t('admin.promptStrategyOverview')}</h4>
                  <div className="strategy-steps">
                    <div className="strategy-step">
                      <div className="step-number">1</div>
                      <div>
                        <strong>{t('admin.systemPromptGlobal')}</strong>
                        <p>{t('admin.systemPromptDescription')}</p>
                      </div>
                    </div>

                    <div className="strategy-step">
                      <div className="step-number primary">2</div>
                      <div>
                        <strong>{t('admin.tenantPromptYourOrg')}</strong>
                        <p>{t('admin.tenantPromptDescription')}</p>
                      </div>
                    </div>

                    <div className="strategy-step">
                      <div className="step-number">3</div>
                      <div>
                        <strong>{t('admin.userPromptPersonal')}</strong>
                        <p>{t('admin.userPromptDescription')}</p>
                      </div>
                    </div>

                    <div className="strategy-step">
                      <div className="step-number">4</div>
                      <div>
                        <strong>{t('admin.agentPromptSpecialized')}</strong>
                        <p>{t('admin.agentPromptDescription')}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Analytics Tab */}
          {currentTab === 'analytics' && (
            <div className="tab-content active" style={{ overflow: 'auto', maxHeight: 'calc(100vh - 200px)' }}>
              <div className="card analytics-card" style={{ overflow: 'visible', paddingBottom: '30px', minHeight: 'fit-content' }}>
                <div className="card-header">
                  <h3>{t('admin.agentUsageAnalytics')}</h3>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)', flexWrap: 'wrap' }}>
                    {/* Agent selector */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                      <label style={{ fontSize: 'var(--text-sm)', color: 'var(--claude-text-muted)' }}>Agent:</label>
                      <Select
                        showSearch
                        placeholder={t('admin.selectAnAgent')}
                        value={selectedAnalyticsBot || undefined}
                        onChange={(value) => handleAnalyticsBotChange(value || '')}
                        filterOption={(input, option) =>
                          (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                        }
                        options={allBots.map(bot => ({
                          value: bot.chatbot_id,
                          label: bot.chatbot_name
                        }))}
                        style={{ minWidth: '180px' }}
                        allowClear
                      />
                    </div>

                    {/* Time range selector */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                      <label style={{ fontSize: 'var(--text-sm)', color: 'var(--claude-text-muted)' }}>Range:</label>
                      <div style={{ display: 'flex', gap: '2px', background: 'var(--claude-bg-secondary)', borderRadius: 'var(--radius)', padding: '2px' }}>
                        {[7, 14, 30, 60, 90].map(days => (
                          <button
                            key={days}
                            onClick={() => handleTimeRangeChange(days)}
                            style={{
                              padding: '4px 10px',
                              fontSize: 'var(--text-xs)',
                              border: 'none',
                              borderRadius: 'calc(var(--radius) - 2px)',
                              background: analyticsTimeRange === days ? 'var(--primary)' : 'transparent',
                              color: analyticsTimeRange === days ? '#fff' : 'var(--claude-text-muted)',
                              cursor: 'pointer',
                              fontWeight: analyticsTimeRange === days ? 600 : 400,
                              transition: 'all 0.15s ease'
                            }}
                          >
                            {days}d
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Reset zoom button */}
                    {(brushStart !== null && brushEnd !== null) && (
                      <button
                        onClick={resetBrushSelection}
                        style={{
                          padding: '4px 12px',
                          fontSize: 'var(--text-xs)',
                          border: '1px solid var(--claude-border)',
                          borderRadius: 'var(--radius)',
                          background: 'var(--claude-bg-tertiary)',
                          color: 'var(--claude-text-primary)',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}
                      >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
                          <path d="M3 3v5h5" />
                        </svg>
                        {t('admin.resetZoom')}
                      </button>
                    )}
                  </div>
                </div>

                <div className="analytics-content" style={{ padding: 'var(--space-4)', flex: 1, display: 'flex', flexDirection: 'column' }}>
                  {!selectedAnalyticsBot ? (
                    <div className="analytics-empty" style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flex: 1,
                      color: 'var(--claude-text-muted)',
                      gap: 'var(--space-3)'
                    }}>
                      <svg viewBox="0 0 24 24" width="64" height="64" style={{ opacity: 0.5 }} fill="none" stroke="currentColor" strokeWidth="1.5">
                        <path d="M3 3v18h18" />
                        <path d="M7 16l4-4 4 4 5-6" />
                      </svg>
                      <p style={{ fontSize: 'var(--text-base)', margin: 0 }}>{t('admin.selectAgentForAnalytics')}</p>
                      <p style={{ fontSize: 'var(--text-sm)', margin: 0, opacity: 0.7 }}>{t('admin.tokenUsageOverRange')}</p>
                    </div>
                  ) : analyticsLoading ? (
                    <div className="analytics-loading" style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flex: 1
                    }}>
                      <div style={{ textAlign: 'center', color: 'var(--claude-text-muted)' }}>
                        <div className="loading-spinner" style={{ width: 40, height: 40, margin: '0 auto var(--space-3)' }} />
                        <p>{t('admin.loadingAnalytics')}</p>
                      </div>
                    </div>
                  ) : (
                    <div className="analytics-chart-container" style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'visible', paddingBottom: 'var(--space-4)' }}>
                      {/* Stats Summary */}
                      <div className="analytics-stats" style={{
                        display: 'flex',
                        gap: 'var(--space-4)',
                        marginBottom: 'var(--space-4)',
                        flexWrap: 'wrap'
                      }}>
                        <div className="analytics-stat-card" style={{
                          background: 'var(--claude-bg-tertiary)',
                          padding: 'var(--space-3) var(--space-4)',
                          borderRadius: 'var(--radius)',
                          border: '1px solid var(--claude-border)',
                          minWidth: '140px'
                        }}>
                          <div style={{ fontSize: 'var(--text-xs)', color: 'var(--claude-text-muted)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{t('admin.totalTokens')}</div>
                          <div style={{ fontSize: 'var(--text-xl)', fontWeight: 600, color: 'var(--claude-text-primary)' }}>
                            {(() => {
                              const total = dailyUsageData.reduce((sum, d) => sum + d.tokens, 0);
                              if (total >= 1000000) return `${(total / 1000000).toFixed(1)}M`;
                              if (total >= 1000) return `${(total / 1000).toFixed(1)}K`;
                              return total.toLocaleString();
                            })()}
                          </div>
                        </div>
                        <div className="analytics-stat-card" style={{
                          background: 'var(--claude-bg-tertiary)',
                          padding: 'var(--space-3) var(--space-4)',
                          borderRadius: 'var(--radius)',
                          border: '1px solid var(--claude-border)',
                          minWidth: '140px'
                        }}>
                          <div style={{ fontSize: 'var(--text-xs)', color: 'var(--claude-text-muted)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{t('admin.totalMessages')}</div>
                          <div style={{ fontSize: 'var(--text-xl)', fontWeight: 600, color: 'var(--claude-text-primary)' }}>
                            {dailyUsageData.reduce((sum, d) => sum + d.messages, 0).toLocaleString()}
                          </div>
                        </div>
                        <div className="analytics-stat-card" style={{
                          background: 'var(--claude-bg-tertiary)',
                          padding: 'var(--space-3) var(--space-4)',
                          borderRadius: 'var(--radius)',
                          border: '1px solid var(--claude-border)',
                          minWidth: '140px'
                        }}>
                          <div style={{ fontSize: 'var(--text-xs)', color: 'var(--claude-text-muted)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{t('admin.conversations')}</div>
                          <div style={{ fontSize: 'var(--text-xl)', fontWeight: 600, color: 'var(--claude-text-primary)' }}>
                            {dailyUsageData.reduce((sum, d) => sum + d.conversations, 0).toLocaleString()}
                          </div>
                        </div>
                        <div className="analytics-stat-card" style={{
                          background: 'var(--claude-bg-tertiary)',
                          padding: 'var(--space-3) var(--space-4)',
                          borderRadius: 'var(--radius)',
                          border: '1px solid var(--claude-border)',
                          minWidth: '140px'
                        }}>
                          <div style={{ fontSize: 'var(--text-xs)', color: 'var(--claude-text-muted)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{t('admin.avgTokensPerDay')}</div>
                          <div style={{ fontSize: 'var(--text-xl)', fontWeight: 600, color: 'var(--claude-text-primary)' }}>
                            {(() => {
                              const avg = Math.round(dailyUsageData.reduce((sum, d) => sum + d.tokens, 0) / Math.max(dailyUsageData.length, 1));
                              if (avg >= 1000000) return `${(avg / 1000000).toFixed(1)}M`;
                              if (avg >= 1000) return `${(avg / 1000).toFixed(1)}K`;
                              return avg.toLocaleString();
                            })()}
                          </div>
                        </div>
                      </div>

                      {/* Chart with drag-to-zoom on main chart */}
                      <div className="chart-wrapper" style={{
                        background: 'var(--claude-bg-tertiary)',
                        borderRadius: 'var(--radius)',
                        border: '1px solid var(--claude-border)',
                        padding: 'var(--space-4)'
                      }}>
                        {dailyUsageData.length > 0 && (() => {
                          // Determine visible data range
                          const visibleStart = brushStart ?? 0;
                          const visibleEnd = brushEnd ?? (dailyUsageData.length - 1);
                          const visibleData = dailyUsageData.slice(visibleStart, visibleEnd + 1);
                          const isZoomed = brushStart !== null && brushEnd !== null;

                          const maxTokens = Math.max(...visibleData.map(d => d.tokens), 1);

                          // Chart dimensions - compact viewBox with space for date labels
                          const chartHeight = 280;
                          const padding = { top: 15, right: 40, bottom: 50, left: 55 };

                          // Format number for Y-axis
                          const formatNumber = (n: number) => {
                            if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
                            if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
                            return n.toString();
                          };

                          // Handle drag selection on main chart
                          const handleChartMouseDown = (e: React.MouseEvent<SVGRectElement>) => {
                            const svg = e.currentTarget.ownerSVGElement;
                            if (!svg) return;
                            const rect = svg.getBoundingClientRect();
                            const svgWidth = 800; // viewBox width
                            const scale = svgWidth / rect.width;
                            const x = (e.clientX - rect.left) * scale - padding.left;
                            setIsDragging(true);
                            setDragStartX(x);
                          };

                          const handleChartMouseMove = (e: React.MouseEvent<SVGSVGElement>, innerWidth: number) => {
                            if (!isDragging || dragStartX === null) return;
                            const rect = e.currentTarget.getBoundingClientRect();
                            const svgWidth = 800;
                            const scale = svgWidth / rect.width;
                            const currentX = Math.max(0, Math.min((e.clientX - rect.left) * scale - padding.left, innerWidth));
                            setDragCurrentX(currentX);
                          };

                          const handleChartMouseUp = (innerWidth: number) => {
                            if (isDragging && dragStartX !== null && dragCurrentX !== null) {
                              const startX = Math.max(0, Math.min(dragStartX, innerWidth));
                              const endX = Math.max(0, Math.min(dragCurrentX, innerWidth));

                              // Map X positions to data indices (relative to current visible data)
                              const startRatio = Math.min(startX, endX) / innerWidth;
                              const endRatio = Math.max(startX, endX) / innerWidth;

                              const startIdx = visibleStart + Math.round(startRatio * (visibleData.length - 1));
                              const endIdx = visibleStart + Math.round(endRatio * (visibleData.length - 1));

                              if (endIdx - startIdx >= 1) {
                                setBrushStart(startIdx);
                                setBrushEnd(endIdx);
                              }
                            }
                            setIsDragging(false);
                            setDragStartX(null);
                            setDragCurrentX(null);
                          };

                          return (
                            <div>
                              {/* Chart Title with zoom info */}
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-2)' }}>
                                <div style={{ fontSize: 'var(--text-sm)', color: 'var(--claude-text-muted)' }}>
                                  {t('admin.dailyTokenUsage')} {isZoomed
                                    ? `(${new Date(visibleData[0]?.date).toLocaleDateString(activeLocale(), { month: 'short', day: 'numeric' })} - ${new Date(visibleData[visibleData.length - 1]?.date).toLocaleDateString(activeLocale(), { month: 'short', day: 'numeric' })})`
                                    : `(${t('admin.lastNDays', { count: analyticsTimeRange })})`}
                                </div>
                                <div style={{ fontSize: 'var(--text-xs)', color: 'var(--claude-text-muted)', opacity: 0.7 }}>
                                  {t('admin.dragToZoom')}{isZoomed ? t('admin.doubleClickToReset') : ''}
                                </div>
                              </div>

                              {/* Main Chart with drag selection */}
                              <div style={{ marginBottom: '20px' }}>
                              <svg
                                width="100%"
                                viewBox="0 0 800 280"
                                style={{ cursor: 'crosshair', userSelect: 'none', display: 'block', overflow: 'visible' }}
                                onMouseMove={(e) => handleChartMouseMove(e, 800 - padding.left - padding.right)}
                                onMouseUp={() => handleChartMouseUp(800 - padding.left - padding.right)}
                                onMouseLeave={() => handleChartMouseUp(800 - padding.left - padding.right)}
                                onDoubleClick={() => resetBrushSelection()}
                              >
                                <defs>
                                  <linearGradient id="areaGradientMain" x1="0%" y1="0%" x2="0%" y2="100%">
                                    <stop offset="0%" stopColor="#22c55e" stopOpacity="0.4" />
                                    <stop offset="100%" stopColor="#22c55e" stopOpacity="0.02" />
                                  </linearGradient>
                                  <linearGradient id="lineGradientMain" x1="0%" y1="0%" x2="100%" y2="0%">
                                    <stop offset="0%" stopColor="#16a34a" />
                                    <stop offset="100%" stopColor="#22c55e" />
                                  </linearGradient>
                                </defs>

                                {(() => {
                                  const innerWidth = 800 - padding.left - padding.right;
                                  const innerHeight = chartHeight - padding.top - padding.bottom;

                                  const points = visibleData.map((day, index) => {
                                    const x = visibleData.length > 1 ? (index / (visibleData.length - 1)) * innerWidth : innerWidth / 2;
                                    const y = innerHeight - (day.tokens / maxTokens) * innerHeight;
                                    return { x, y, data: day, index };
                                  });

                                  const linePath = points.map((p, idx) => `${idx === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
                                  // areaPath available for future use: `${linePath} L ${points[points.length - 1]?.x ?? 0} ${innerHeight} L 0 ${innerHeight} Z`
                                  void linePath; // Used for area chart if needed

                                  return (
                                    <>
                                      {/* Chart frame */}
                                      <rect x={padding.left} y={padding.top} width={innerWidth} height={innerHeight} fill="var(--claude-bg-secondary)" stroke="var(--claude-border)" strokeWidth="1" />

                                      <g transform={`translate(${padding.left}, ${padding.top})`}>
                                        {/* Grid lines */}
                                        {[0.25, 0.5, 0.75].map((ratio, i) => (
                                          <line key={i} x1={0} y1={ratio * innerHeight} x2={innerWidth} y2={ratio * innerHeight} stroke="var(--claude-border)" strokeOpacity={0.3} strokeDasharray="4,4" />
                                        ))}

                                        {/* Daily usage bars - better visualization for discrete daily data */}
                                        {points.map((point) => {
                                          const barWidth = Math.max(2, (innerWidth / points.length) * 0.7);
                                          const barHeight = point.data.tokens > 0 ? (point.data.tokens / maxTokens) * innerHeight : 0;
                                          const barX = point.x - barWidth / 2;
                                          const barY = innerHeight - barHeight;

                                          return (
                                            <g key={point.data.date}>
                                              {/* Bar with gradient fill */}
                                              <rect
                                                x={barX}
                                                y={barY}
                                                width={barWidth}
                                                height={barHeight}
                                                fill="url(#areaGradientMain)"
                                                stroke="#22c55e"
                                                strokeWidth={barHeight > 0 ? 1 : 0}
                                                rx={2}
                                                style={{ cursor: 'pointer' }}
                                              >
                                                <title>{`${point.data.date}\nTokens: ${point.data.tokens.toLocaleString()}\nMessages: ${point.data.messages}\nConversations: ${point.data.conversations}`}</title>
                                              </rect>
                                            </g>
                                          );
                                        })}

                                        {/* Drag selection overlay - interactive area */}
                                        <rect
                                          x={0}
                                          y={0}
                                          width={innerWidth}
                                          height={innerHeight}
                                          fill="transparent"
                                          style={{ cursor: 'crosshair' }}
                                          onMouseDown={(e) => handleChartMouseDown(e)}
                                        />

                                        {/* Selection highlight rectangle while dragging */}
                                        {isDragging && dragStartX !== null && dragCurrentX !== null && (
                                          <rect
                                            x={Math.min(dragStartX, dragCurrentX)}
                                            y={0}
                                            width={Math.abs(dragCurrentX - dragStartX)}
                                            height={innerHeight}
                                            fill="#22c55e"
                                            opacity={0.2}
                                            stroke="#22c55e"
                                            strokeWidth={2}
                                            rx={4}
                                          />
                                        )}
                                      </g>

                                      {/* Y-axis labels */}
                                      <text x={padding.left - 8} y={padding.top + 4} textAnchor="end" fontSize="11" fill="var(--claude-text-muted)">{formatNumber(maxTokens)}</text>
                                      <text x={padding.left - 8} y={padding.top + innerHeight / 2} textAnchor="end" fontSize="11" fill="var(--claude-text-muted)">{formatNumber(maxTokens / 2)}</text>
                                      <text x={padding.left - 8} y={padding.top + innerHeight - 4} textAnchor="end" fontSize="11" fill="var(--claude-text-muted)">0</text>

                                      {/* X-axis labels - positioned below the chart frame with edge protection */}
                                      {visibleData.length > 0 && [0, 0.25, 0.5, 0.75, 1].map((ratio, i) => {
                                        const idx = Math.min(Math.round(ratio * (visibleData.length - 1)), visibleData.length - 1);
                                        const dateStr = visibleData[idx]?.date;
                                        if (!dateStr) return null;
                                        const date = new Date(dateStr);
                                        // Adjust text anchor for edge labels to prevent clipping
                                        const textAnchor = ratio === 0 ? 'start' : ratio === 1 ? 'end' : 'middle';
                                        return (
                                          <text key={i} x={padding.left + ratio * innerWidth} y={padding.top + innerHeight + 25} textAnchor={textAnchor} fontSize="13" fill="var(--claude-text-muted)">
                                            {date.toLocaleDateString(activeLocale(), { month: 'short', day: 'numeric' })}
                                          </text>
                                        );
                                      })}
                                    </>
                                  );
                                })()}
                              </svg>
                              </div>
                            </div>
                          );
                        })()}

                        {dailyUsageData.length === 0 && (
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: '220px',
                            color: 'var(--claude-text-muted)'
                          }}>
                            {t('admin.noUsageDataAvailableFor')}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Agent Insights Tab */}
          {currentTab === 'insights' && (
            <div className="tab-content active">
              <div className="card" style={{ height: 'calc(100vh - 280px)', display: 'flex', flexDirection: 'column' }}>
                <div className="card-header" style={{ flexShrink: 0 }}>
                  <h3>{t('admin.agentInsights')}</h3>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                    <label style={{ fontSize: 'var(--text-sm)', color: 'var(--claude-text-muted)' }}>Agent:</label>
                    <Select
                      showSearch
                      placeholder={t('admin.selectAnAgent')}
                      value={selectedInsightsBot || undefined}
                      onChange={(value) => handleInsightsBotChange(value || '')}
                      filterOption={(input, option) =>
                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                      }
                      options={insightsBots.map(bot => ({
                        value: bot.chatbot_id,
                        label: bot.chatbot_name
                      }))}
                      style={{ minWidth: '200px' }}
                      allowClear
                    />
                    {insightsSummariesLoading && (
                      <span style={{ fontSize: 'var(--text-xs)', color: 'var(--claude-text-muted)' }}>
                        {t('admin.loadingSummaries')}
                      </span>
                    )}
                    {selectedInsightsBot && !insightsSummariesLoading && insightsSummaries.length > 0 && (
                      <span style={{ fontSize: 'var(--text-xs)', color: 'var(--claude-text-muted)' }}>
                        {insightsSummaries.length} conversation summaries loaded
                      </span>
                    )}
                    {/* Clear chat button */}
                    {insightsMessages.length > 0 && (
                      <Button
                        size="small"
                        onClick={() => setInsightsMessages([])}
                        style={{ marginInlineStart: 'auto' }}
                      >
                        {t('admin.clearChat')}
                      </Button>
                    )}
                  </div>
                </div>

                {/* Chat Area */}
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', padding: 'var(--space-4)' }}>
                  {!selectedInsightsBot ? (
                    <div style={{
                      flex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'var(--claude-text-muted)',
                      flexDirection: 'column',
                      gap: 'var(--space-3)'
                    }}>
                      <svg viewBox="0 0 24 24" width="48" height="48" style={{ opacity: 0.5 }}>
                        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" fill="none" stroke="currentColor" strokeWidth="2" />
                      </svg>
                      <p>{t('admin.selectAgentForSummaries')}</p>
                    </div>
                  ) : insightsSummaries.length === 0 && !insightsSummariesLoading ? (
                    <div style={{
                      flex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'var(--claude-text-muted)',
                      flexDirection: 'column',
                      gap: 'var(--space-3)'
                    }}>
                      <svg viewBox="0 0 24 24" width="48" height="48" style={{ opacity: 0.5 }}>
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" fill="none" stroke="currentColor" strokeWidth="2" />
                        <polyline points="14,2 14,8 20,8" fill="none" stroke="currentColor" strokeWidth="2" />
                      </svg>
                      <p>{t('admin.noSummariesForAgent')}</p>
                    </div>
                  ) : (
                    <>
                      {/* Messages Area */}
                      <div style={{
                        flex: 1,
                        overflowY: 'auto',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 'var(--space-4)',
                        paddingBottom: 'var(--space-4)'
                      }}>
                        {insightsMessages.length === 0 ? (
                          <div style={{
                            flex: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'var(--claude-text-muted)',
                            flexDirection: 'column',
                            gap: 'var(--space-3)'
                          }}>
                            <p style={{ fontSize: 'var(--text-lg)', fontWeight: 500 }}>
                              {t('admin.askQuestionsAboutYourAgent')}
                            </p>
                            <p style={{ fontSize: 'var(--text-sm)', maxWidth: '500px', textAlign: 'center' }} dir="auto">
                              {t('admin.insightsExamples')}
                            </p>
                          </div>
                        ) : (
                          insightsMessages.map((msg, idx) => (
                            <div key={idx} className={`message-group ${msg.role}`}>
                              {msg.role === 'assistant' && (
                                <div className="message-avatar">
                                  <svg viewBox="0 0 24 24" width="16" height="16">
                                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" fill="none" stroke="currentColor" strokeWidth="2" />
                                  </svg>
                                </div>
                              )}
                              <div className="message-content">
                                <div className="message-text" style={{ whiteSpace: 'pre-wrap' }} dir="auto">
                                  {msg.content}
                                </div>
                              </div>
                            </div>
                          ))
                        )}
                        {insightsLoading && (
                          <div className="message-group assistant">
                            <div className="message-avatar">
                              <svg viewBox="0 0 24 24" width="16" height="16">
                                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" fill="none" stroke="currentColor" strokeWidth="2" />
                              </svg>
                            </div>
                            <div className="message-content">
                              <div className="typing-indicator-content">
                                <div className="typing-dots">
                                  <div className="typing-dot"></div>
                                  <div className="typing-dot"></div>
                                  <div className="typing-dot"></div>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                        {/* Auto-scroll anchor */}
                        <div ref={insightsMessagesEndRef} />
                      </div>

                      {/* Input Area - Using ChatInput styling */}
                      <form onSubmit={handleInsightsSubmit} style={{ flexShrink: 0 }}>
                        <div className="chat-input-wrapper">
                          <div className="chat-input-group">
                            <div className="chat-input-section">
                              <input dir="auto"
                                type="text"
                                className="chat-input"
                                value={insightsQuery}
                                onChange={(e) => setInsightsQuery(e.target.value)}
                                placeholder={t('admin.askAboutAgent')}
                                disabled={insightsLoading || insightsSummaries.length === 0}
                              />
                            </div>
                            <div className="input-controls">
                              <button
                                type="submit"
                                className="send-button"
                                disabled={insightsLoading || !insightsQuery.trim() || insightsSummaries.length === 0}
                              >
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" />
                                </svg>
                              </button>
                            </div>
                          </div>
                        </div>
                      </form>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Skills Tab - Coming Soon */}
          {currentTab === 'skills' && (
            <div className="tab-content active">
              <div className="card" style={{
                minHeight: '400px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                padding: 'var(--space-8)'
              }}>
                <div style={{ maxWidth: '500px' }}>
                  {/* Icon */}
                  <div style={{
                    marginBottom: 'var(--space-6)',
                    color: '#3b82f6',
                    opacity: 0.5
                  }}>
                    <svg viewBox="0 0 24 24" width="80" height="80" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M22 10v6M2 10l10-5 10 5-10 5z" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M6 12v5c0 1 2 3 6 3s6-2 6-3v-5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>

                  {/* Title */}
                  <h2 style={{
                    fontSize: 'var(--text-2xl)',
                    fontWeight: 600,
                    color: 'var(--claude-text-primary)',
                    marginBottom: 'var(--space-3)'
                  }}>
                    {t('skills.title')}
                  </h2>

                  {/* Description */}
                  <p style={{
                    fontSize: 'var(--text-base)',
                    color: 'var(--claude-text-muted)',
                    marginBottom: 'var(--space-4)',
                    lineHeight: 1.6
                  }}>
                    {t('admin.theSkillsManagementSystemIs')}
                  </p>

                  {/* Coming Soon Badge */}
                  <div style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 'var(--space-2)',
                    padding: 'var(--space-2) var(--space-4)',
                    background: 'rgba(59, 130, 246, 0.1)',
                    border: '1px solid rgba(59, 130, 246, 0.2)',
                    borderRadius: 'var(--radius-full)',
                    color: '#3b82f6',
                    fontSize: 'var(--text-sm)',
                    fontWeight: 500
                  }}>
                    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10" />
                      <polyline points="12 6 12 12 16 14" />
                    </svg>
                    {t('common.comingSoon')}
                  </div>
                </div>
              </div>
            </div>
          )}
          {/* System Design Tab (super_admin / group_admin only) — the
              subtree's brand book: same panels, same effect as the
              super-admin White-Label Editor, through subtree-scoped routes. */}
          {currentTab === 'system-design' && (isSuperAdmin || isGroupAdmin) && (
            <div className="tab-content active">
              <SystemDesignTab tenants={tenants} />
            </div>
          )}

          {/* Connector library (super_admin / group_admin): the organization's
              connector DEFAULTS, picked from the agent editor's Connectors tab. */}
          {currentTab === 'connector-library' && libraryTabAllowed && (
            <div className="tab-content active">
              <ConnectorLibraryTab tenants={tenants} />
            </div>
          )}

          {/* Tenant Context Tab (super_admin / group_admin only) */}
          {currentTab === 'tenant-context' && (isSuperAdmin || isGroupAdmin) && tenantContextFeatureEnabled && (
            <div className="tab-content active">
              <TenantContextEditor
                tenants={tenants}
                loading={hierarchyLoading}
                hierarchyTree={hierarchyTree}
                onGetContextPrompt={groupAdminService.getTenantContextPrompt}
                onUpdateContextPrompt={groupAdminService.updateTenantContextPrompt}
                onPreviewContext={groupAdminService.previewTenantContext}
                onListFiles={groupAdminService.listTenantContextFiles}
                onUploadFile={groupAdminService.uploadTenantContextFile}
                onDeleteFile={groupAdminService.deleteTenantContextFile}
                onUpdateFile={groupAdminService.updateTenantContextFile}
                onAddCloudFiles={groupAdminService.addTenantCloudFiles}
                onListDataBundles={groupAdminService.listTenantDataBundles}
                onUpdateDataBundles={groupAdminService.updateTenantDataBundles}
                onRemoveDataBundle={groupAdminService.removeTenantDataBundle}
                onUpdateDataBundle={groupAdminService.updateTenantDataBundle}
                onUpdateFilesContextPrompt={groupAdminService.updateTenantFilesContextPrompt}
                onRegenerateContext={groupAdminService.regenerateTenantContext}
              />
            </div>
          )}
        </main>
      </div>

      {/* Create User Modal */}
      {showCreateUserModal && (
        <div className="modal-overlay" onClick={() => setShowCreateUserModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">{t('admin.addNewUser')}</h3>
              <button
                className="close-btn"
                onClick={() => setShowCreateUserModal(false)}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleCreateUser}>
              <div className="modal-content">
                <ModalErrorBanner ref={createUserErrorRef} message={createUserError} />
                <div className="form-group">
                  <label htmlFor="user-tenant">{t('admin.tenants')}</label>
                  {/* Hierarchy-styled searchable select — matches the
                      "share bot to tenant" picker, and renders inside the
                      modal so it is never hidden behind the overlay. */}
                  <div className="searchable-select" ref={userTenantDropdownRef}>
                    <button
                      type="button"
                      id="user-tenant"
                      className={`select-trigger ${isUserTenantDropdownOpen ? 'open' : ''} ${createUserForm.tenant_id ? 'has-value' : ''}`}
                      onClick={() => setIsUserTenantDropdownOpen(!isUserTenantDropdownOpen)}
                    >
                      <span className="select-value" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        {createUserForm.tenant_id
                          ? (() => {
                              const selectedTenant = tenants.find(t => t.tenant_id === createUserForm.tenant_id);
                              if (selectedTenant) {
                                const baseDepth = tenants.length > 0 ? Math.min(...tenants.map(t => t.depth ?? 0)) : 0;
                                const relativeLevel = (selectedTenant.depth ?? 0) - baseDepth;
                                const style = levelStyles[Math.min(relativeLevel, levelStyles.length - 1)];
                                return (<>{getLevelIcon(relativeLevel, style.color)}<span dir="auto">{selectedTenant.tenant_name}</span></>);
                              }
                              return t('admin.selectTenantPlaceholder');
                            })()
                          : t('admin.selectTenantPlaceholder')}
                      </span>
                      <svg className="select-arrow" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="6 9 12 15 18 9" />
                      </svg>
                    </button>

                    {isUserTenantDropdownOpen && (
                      <div className="select-dropdown">
                        <div className="select-search">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="11" cy="11" r="8" />
                            <path d="m21 21-4.3-4.3" />
                          </svg>
                          <input dir="auto"
                            type="text"
                            placeholder={t('admin.searchTenants')}
                            value={userTenantSearchQuery}
                            onChange={(e) => setUserTenantSearchQuery(e.target.value)}
                            autoFocus
                          />
                        </div>
                        <div className="select-options">
                          {(() => {
                            const filteredTenants = tenants.filter(tenant =>
                              tenant.tenant_name.toLowerCase().includes(userTenantSearchQuery.toLowerCase()) ||
                              tenant.tenant_code.toLowerCase().includes(userTenantSearchQuery.toLowerCase())
                            ).sort((a, b) => (a.depth ?? 0) - (b.depth ?? 0));

                            if (filteredTenants.length === 0) {
                              return (
                                <div className="select-no-results">
                                  {tenantsLoadError
                                    ? tenantsLoadError
                                    : userTenantSearchQuery
                                      ? `No tenants found matching "${userTenantSearchQuery}"`
                                      : t('admin.userManagement.noTenantsAvailable', { defaultValue: 'No organizations available' })}
                                </div>
                              );
                            }

                            const baseDepth = tenants.length > 0 ? Math.min(...tenants.map(t => t.depth ?? 0)) : 0;

                            return filteredTenants.map((tenant) => {
                              const relativeLevel = (tenant.depth ?? 0) - baseDepth;
                              const hierarchyPath = buildTenantHierarchyPath(tenant);
                              const style = levelStyles[Math.min(relativeLevel, levelStyles.length - 1)];
                              return (
                                <button
                                  key={tenant.tenant_id}
                                  type="button"
                                  className={`select-option ${createUserForm.tenant_id === tenant.tenant_id ? 'selected' : ''}`}
                                  onClick={() => {
                                    setCreateUserForm({ ...createUserForm, tenant_id: tenant.tenant_id });
                                    setIsUserTenantDropdownOpen(false);
                                    setUserTenantSearchQuery('');
                                  }}
                                  style={{ borderInlineStartColor: style.color, borderInlineStartWidth: '3px', borderInlineStartStyle: 'solid' }}
                                >
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                                    {getLevelIcon(relativeLevel, style.color)}
                                    <div className="option-info" style={{ flex: 1 }}>
                                      <span dir="auto" className="option-name">
                                        {tenant.tenant_name}
                                        <span className="option-badge" style={{ background: style.bgColor, color: style.color }}>
                                          {relativeLevel === 0 ? 'ROOT' : `Level ${relativeLevel}`}
                                        </span>
                                      </span>
                                      <span dir="auto" className="option-meta">{hierarchyPath}</span>
                                    </div>
                                  </div>
                                  {createUserForm.tenant_id === tenant.tenant_id && (
                                    <svg className="option-check" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                      <polyline points="20 6 9 17 4 12" />
                                    </svg>
                                  )}
                                </button>
                              );
                            });
                          })()}
                        </div>
                      </div>
                    )}
                  </div>
                  <small style={{ color: 'var(--text-muted)', marginTop: '4px', display: 'block' }}>
                    {tenantsLoadError || t('admin.selectTenantForUser')}
                  </small>
                </div>

                <div className="form-group">
                  <label htmlFor="user-email">{t('admin.emailAddress')}</label>
                  <input dir="auto"
                    type="email"
                    id="user-email"
                    className="input"
                    required
                    placeholder="user@example.com"
                    value={createUserForm.email}
                    onChange={(e) =>
                      setCreateUserForm({ ...createUserForm, email: e.target.value })
                    }
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="user-full-name">{t('admin.displayName')}</label>
                  <input dir="auto"
                    type="text"
                    id="user-full-name"
                    className="input"
                    placeholder={t('admin.fullNamePlaceholder')}
                    value={createUserForm.full_name}
                    onChange={(e) =>
                      setCreateUserForm({
                        ...createUserForm,
                        full_name: e.target.value,
                      })
                    }
                  />
                </div>

                {canInvite && (
                  <div className="form-group">
                    <label>{t('admin.userManagement.accessSetup', { defaultValue: 'Access setup' })}</label>
                    <Radio.Group
                      value={createUserMode}
                      onChange={(e) => setCreateUserMode(e.target.value)}
                    >
                      <Radio value="password">{t('admin.userManagement.setPasswordNow', { defaultValue: 'Set password now' })}</Radio>
                      <Radio value="invite" disabled={!createUserInviteAllowed}>{t('admin.userManagement.sendInvitationEmail', { defaultValue: 'Send invitation email' })}</Radio>
                    </Radio.Group>
                    {!createUserInviteAllowed && (
                      <small style={{ color: 'var(--text-muted)', marginTop: '4px', display: 'block' }}>
                        {t('admin.userManagement.inviteDisabledForTenant', { defaultValue: 'Email invitations are disabled for this tenant.' })}
                      </small>
                    )}
                    {createUserInviteAllowed && createUserMode === 'invite' && (
                      <small style={{ color: 'var(--text-muted)', marginTop: '4px', display: 'block' }}>
                        {t('admin.userManagement.inviteHelper', { defaultValue: 'The user will receive an email with a link (valid 7 days) to set their own password' })}
                      </small>
                    )}
                  </div>
                )}

                {createUserMode === 'password' && (
                  <div className="form-group">
                    <label htmlFor="user-password">{t('admin.userManagement.passwordLabel', { defaultValue: 'Password' })}</label>
                    <input dir="auto"
                      type="password"
                      id="user-password"
                      className="input"
                      required
                      placeholder={t('admin.userManagement.enterPassword', { defaultValue: 'Enter password' })}
                      minLength={8}
                      value={createUserForm.password}
                      onChange={(e) =>
                        setCreateUserForm({ ...createUserForm, password: e.target.value })
                      }
                    />
                    <small style={{ color: 'var(--text-muted)' }}>
                      {t('admin.userManagement.passwordHint', { defaultValue: '8+ characters with a letter, a number, and a symbol' })}
                    </small>
                  </div>
                )}

                {/* Role info note */}
                <div style={{
                  background: 'var(--claude-bg-secondary)',
                  border: '1px solid var(--claude-border)',
                  borderRadius: 'var(--radius)',
                  padding: '10px 14px',
                  marginTop: '8px',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '8px'
                }}>
                  <svg viewBox="0 0 24 24" width="16" height="16" style={{ fill: 'none', stroke: 'var(--claude-text-muted)', strokeWidth: 2, flexShrink: 0, marginTop: '1px' }}>
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="16" x2="12" y2="12" />
                    <line x1="12" y1="8" x2="12.01" y2="8" />
                  </svg>
                  <div style={{ fontSize: 'var(--text-sm)', color: 'var(--claude-text-muted)' }}>
                    {t('admin.newUsersCreatedWithPrefix')}<strong style={{ color: 'var(--claude-text-secondary)' }}>{t('admin.userRole')}</strong>{t('admin.newUsersCreatedWithSuffix')}
                    {t('admin.changeRoleLater')}
                  </div>
                </div>
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setShowCreateUserModal(false)}
                >
                  {t('vault.cancel')}
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={!createUserForm.tenant_id || createUserSubmitting}
                >
                  {createUserSubmitting
                    ? t('common.saving', { defaultValue: 'Saving…' })
                    : createUserMode === 'invite'
                      ? t('admin.userManagement.sendInvitation', { defaultValue: 'Send Invitation' })
                      : t('admin.addUser', { defaultValue: 'Add User' })}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Create User in Tenant Modal (Organization tab) */}
      {showTenantUserModal && selectedTenantId && (
        <div className="modal-overlay" onClick={() => setShowTenantUserModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">{t('admin.addNewUser')}</h3>
              <button
                className="close-btn"
                onClick={() => setShowTenantUserModal(false)}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleCreateTenantUser}>
              <div className="modal-content">
                <ModalErrorBanner ref={tenantUserErrorRef} message={tenantUserError} />
                {/* Tenant info banner */}
                <div style={{
                  background: 'var(--claude-bg-tertiary)',
                  border: '1px solid var(--claude-border)',
                  borderRadius: 'var(--radius)',
                  padding: '12px 16px',
                  marginBottom: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px'
                }}>
                  <svg viewBox="0 0 24 24" width="18" height="18" style={{ fill: 'none', stroke: 'var(--claude-accent)', strokeWidth: 2, flexShrink: 0 }}>
                    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                    <polyline points="9 22 9 12 15 12 15 22" />
                  </svg>
                  <div>
                    <div style={{ fontWeight: 500, color: 'var(--claude-text-primary)' }}>
                      {t('admin.creatingUserIn')}<span style={{ color: 'var(--claude-accent)' }}>{selectedTenantName}</span>
                    </div>
                    <div style={{ fontSize: 'var(--text-xs)', color: 'var(--claude-text-muted)', marginTop: '2px' }}>
                      {t('admin.thisUserWillBelongTo')}
                    </div>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="tenant-user-email">{t('admin.emailAddress')}</label>
                  <input dir="auto"
                    type="email"
                    id="tenant-user-email"
                    className="input"
                    required
                    placeholder="user@example.com"
                    value={tenantUserForm.email}
                    onChange={(e) =>
                      setTenantUserForm({ ...tenantUserForm, email: e.target.value })
                    }
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="tenant-user-name">{t('admin.displayName')}</label>
                  <input dir="auto"
                    type="text"
                    id="tenant-user-name"
                    className="input"
                    placeholder={t('admin.fullNamePlaceholder')}
                    value={tenantUserForm.display_name}
                    onChange={(e) =>
                      setTenantUserForm({
                        ...tenantUserForm,
                        display_name: e.target.value,
                      })
                    }
                  />
                </div>

                {canInvite && (
                  <div className="form-group">
                    <label>{t('admin.userManagement.accessSetup', { defaultValue: 'Access setup' })}</label>
                    <Radio.Group
                      value={tenantUserMode}
                      onChange={(e) => setTenantUserMode(e.target.value)}
                    >
                      <Radio value="password">{t('admin.userManagement.setPasswordNow', { defaultValue: 'Set password now' })}</Radio>
                      <Radio value="invite" disabled={!tenantUserInviteAllowed}>{t('admin.userManagement.sendInvitationEmail', { defaultValue: 'Send invitation email' })}</Radio>
                    </Radio.Group>
                    {!tenantUserInviteAllowed && (
                      <small style={{ color: 'var(--text-muted)', marginTop: '4px', display: 'block' }}>
                        {t('admin.userManagement.inviteDisabledForTenant', { defaultValue: 'Email invitations are disabled for this tenant.' })}
                      </small>
                    )}
                    {tenantUserInviteAllowed && tenantUserMode === 'invite' && (
                      <small style={{ color: 'var(--text-muted)', marginTop: '4px', display: 'block' }}>
                        {t('admin.userManagement.inviteHelper', { defaultValue: 'The user will receive an email with a link (valid 7 days) to set their own password' })}
                      </small>
                    )}
                  </div>
                )}

                {tenantUserMode === 'password' && (
                  <div className="form-group">
                    <label htmlFor="tenant-user-password">{t('admin.userManagement.passwordLabel', { defaultValue: 'Password' })}</label>
                    <input dir="auto"
                      type="password"
                      id="tenant-user-password"
                      className="input"
                      required
                      placeholder={t('admin.userManagement.enterPassword', { defaultValue: 'Enter password' })}
                      minLength={8}
                      value={tenantUserForm.password}
                      onChange={(e) =>
                        setTenantUserForm({ ...tenantUserForm, password: e.target.value })
                      }
                    />
                    <small style={{ color: 'var(--text-muted)' }}>
                      {t('admin.userManagement.passwordHint', { defaultValue: '8+ characters with a letter, a number, and a symbol' })}
                    </small>
                  </div>
                )}

                {/* Role info note */}
                <div style={{
                  background: 'var(--claude-bg-secondary)',
                  border: '1px solid var(--claude-border)',
                  borderRadius: 'var(--radius)',
                  padding: '10px 14px',
                  marginTop: '8px',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '8px'
                }}>
                  <svg viewBox="0 0 24 24" width="16" height="16" style={{ fill: 'none', stroke: 'var(--claude-text-muted)', strokeWidth: 2, flexShrink: 0, marginTop: '1px' }}>
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="16" x2="12" y2="12" />
                    <line x1="12" y1="8" x2="12.01" y2="8" />
                  </svg>
                  <div style={{ fontSize: 'var(--text-sm)', color: 'var(--claude-text-muted)' }}>
                    {t('admin.newUsersCreatedWithPrefix')}<strong style={{ color: 'var(--claude-text-secondary)' }}>{t('admin.userRole')}</strong>{t('admin.newUsersCreatedWithSuffix')}
                    {t('admin.changeRoleLater')}
                  </div>
                </div>
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setShowTenantUserModal(false)}
                >
                  {t('vault.cancel')}
                </button>
                <button type="submit" className="btn-primary" disabled={tenantUserSubmitting}>
                  {tenantUserSubmitting
                    ? t('common.saving', { defaultValue: 'Saving…' })
                    : tenantUserMode === 'invite'
                      ? t('admin.userManagement.sendInvitation', { defaultValue: 'Send Invitation' })
                      : t('admin.addUser', { defaultValue: 'Add User' })}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Set User Password Modal (admin sets it directly, no email) */}
      {setPasswordModal.isOpen && (
        <div className="modal-overlay" onClick={closeSetPasswordModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">{t('admin.userManagement.setPassword', { defaultValue: 'Set Password' })}</h3>
              <button className="close-btn" onClick={closeSetPasswordModal}>
                ×
              </button>
            </div>

            <form onSubmit={handleSetPasswordSubmit}>
              <div className="modal-content">
                <p style={{ marginBottom: '16px', color: 'var(--claude-text-muted)' }}>
                  {t('admin.userManagement.setPasswordIntro', { defaultValue: 'Set a new password for' })}{' '}
                  <strong style={{ color: 'var(--claude-text-primary)' }}>{setPasswordModal.userEmail}</strong>.{' '}
                  {t('admin.userManagement.setPasswordWarning', { defaultValue: 'Their existing sessions will be signed out.' })}
                </p>

                {setPasswordError && (
                  <div style={{
                    background: 'var(--claude-bg-secondary)',
                    border: '1px solid var(--error, #dc2626)',
                    borderRadius: 'var(--radius)',
                    padding: '10px 14px',
                    marginBottom: '12px',
                    color: 'var(--error, #dc2626)',
                    fontSize: 'var(--text-sm)',
                  }}>
                    {setPasswordError}
                  </div>
                )}

                <div className="form-group">
                  <label htmlFor="set-user-password">{t('admin.userManagement.newPassword', { defaultValue: 'New Password' })}</label>
                  <input dir="auto"
                    type="password"
                    id="set-user-password"
                    className="input"
                    required
                    placeholder={t('admin.userManagement.enterNewPassword', { defaultValue: 'Enter new password' })}
                    minLength={8}
                    autoComplete="new-password"
                    value={setPasswordForm.password}
                    onChange={(e) =>
                      setSetPasswordForm({ ...setPasswordForm, password: e.target.value })
                    }
                    disabled={setPasswordLoading}
                  />
                  <small style={{ color: 'var(--text-muted)' }}>
                    {t('admin.userManagement.passwordHint', { defaultValue: '8+ characters with a letter, a number, and a symbol' })}
                  </small>
                </div>

                <div className="form-group">
                  <label htmlFor="set-user-password-confirm">{t('admin.userManagement.confirmPassword', { defaultValue: 'Confirm Password' })}</label>
                  <input dir="auto"
                    type="password"
                    id="set-user-password-confirm"
                    className="input"
                    required
                    placeholder={t('admin.userManagement.repeatNewPassword', { defaultValue: 'Repeat the new password' })}
                    minLength={8}
                    autoComplete="new-password"
                    value={setPasswordForm.confirm}
                    onChange={(e) =>
                      setSetPasswordForm({ ...setPasswordForm, confirm: e.target.value })
                    }
                    disabled={setPasswordLoading}
                  />
                </div>
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={closeSetPasswordModal}
                  disabled={setPasswordLoading}
                >
                  {t('common.cancel', { defaultValue: 'Cancel' })}
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={setPasswordLoading}
                >
                  {setPasswordLoading
                    ? t('admin.userManagement.savingPassword', { defaultValue: 'Saving…' })
                    : t('admin.userManagement.setPassword', { defaultValue: 'Set Password' })}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Rename Tenant Modal */}
      {renameTarget && (
        <RenameTenantModal
          tenantId={renameTarget.tenant_id}
          currentName={renameTarget.tenant_name}
          onClose={() => setRenameTarget(null)}
          onRenamed={async () => {
            // The modal stays open on its last step (what changed). Everything
            // on this page that shows organization names reloads, and this tab's
            // own session too: the renamed organization may be the admin's own,
            // or carry the brand this tab wears.
            await Promise.all([loadHierarchy(), loadUsers(), authService.verifyToken(true)]);
          }}
        />
      )}

      {/* Create Tenant Modal */}
      {showCreateTenantModal && (
        <div className="modal-overlay" onClick={() => setShowCreateTenantModal(false)}>
          <div className="modal share-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">{t('admin.createNewTenant')}</h3>
              <button
                className="close-btn"
                onClick={() => setShowCreateTenantModal(false)}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleCreateTenant}>
              <div className="modal-content">
                <div className="form-group">
                  <label htmlFor="parent-tenant">{t('admin.parentTenant')}</label>
                  <div className="searchable-select" ref={parentDropdownRef}>
                    <button
                      type="button"
                      className={`select-trigger ${isParentDropdownOpen ? 'open' : ''} ${createTenantForm.parent_tenant_id ? 'has-value' : ''}`}
                      onClick={() => setIsParentDropdownOpen(!isParentDropdownOpen)}
                    >
                      <span className="select-value" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        {createTenantForm.parent_tenant_id
                          ? (() => {
                              const selectedTenant = tenants.find(t => t.tenant_id === createTenantForm.parent_tenant_id);
                              if (selectedTenant) {
                                const absoluteDepth = selectedTenant.depth ?? 0;
                                // Calculate relative level for visual consistency with organization tree
                                const baseDepth = tenants.length > 0
                                  ? Math.min(...tenants.map(t => t.depth ?? 0))
                                  : 0;
                                const relativeLevel = absoluteDepth - baseDepth;
                                const style = levelStyles[Math.min(relativeLevel, levelStyles.length - 1)];
                                return (
                                  <>
                                    {getLevelIcon(relativeLevel, style.color)}
                                    <span dir="auto">{selectedTenant.tenant_name}</span>
                                  </>
                                );
                              }
                              return t('admin.selectParentTenantPlaceholder');
                            })()
                          : t('admin.selectParentTenantPlaceholder')}
                      </span>
                      <svg className="select-arrow" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="6 9 12 15 18 9" />
                      </svg>
                    </button>

                    {isParentDropdownOpen && (
                      <div className="select-dropdown">
                        <div className="select-search">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="11" cy="11" r="8" />
                            <path d="m21 21-4.3-4.3" />
                          </svg>
                          <input dir="auto"
                            type="text"
                            placeholder={t('admin.searchTenants')}
                            value={parentTenantSearchQuery}
                            onChange={(e) => setParentTenantSearchQuery(e.target.value)}
                            autoFocus
                          />
                        </div>
                        <div className="select-options">
                          {/* Filtered tenant options - show ALL tenants in hierarchy */}
                          {(() => {
                            // Show ALL tenants for super admin, or all tenants visible to the user
                            // Filter out tenants that are at max depth (can't have children)
                            const availableTenants = tenants.filter(t => (t.depth ?? 0) < 4);

                            // Apply search filter
                            const filteredTenants = availableTenants.filter(tenant =>
                              tenant.tenant_name.toLowerCase().includes(parentTenantSearchQuery.toLowerCase()) ||
                              tenant.tenant_code.toLowerCase().includes(parentTenantSearchQuery.toLowerCase())
                            ).sort((a, b) => (a.depth ?? 0) - (b.depth ?? 0));

                            if (filteredTenants.length === 0) {
                              return (
                                <div className="select-no-results">
                                  {parentTenantSearchQuery ? t('admin.noTenantsMatching', { query: parentTenantSearchQuery }) : t('admin.noTenantsAvailable')}
                                </div>
                              );
                            }

                            // Calculate base depth from hierarchy tree root (for relative visual level)
                            // This ensures dropdown icons match the organization tree icons
                            const baseDepth = tenants.length > 0
                              ? Math.min(...tenants.map(t => t.depth ?? 0))
                              : 0;

                            return filteredTenants.map((tenant) => {
                              const absoluteDepth = tenant.depth ?? 0;
                              // Use relative depth for visual consistency with organization tree
                              const relativeLevel = absoluteDepth - baseDepth;
                              const hierarchyPath = buildTenantHierarchyPath(tenant);
                              const style = levelStyles[Math.min(relativeLevel, levelStyles.length - 1)];
                              return (
                                <button
                                  key={tenant.tenant_id}
                                  type="button"
                                  className={`select-option ${createTenantForm.parent_tenant_id === tenant.tenant_id ? 'selected' : ''}`}
                                  onClick={() => {
                                    setCreateTenantForm({ ...createTenantForm, parent_tenant_id: tenant.tenant_id });
                                    setIsParentDropdownOpen(false);
                                    setParentTenantSearchQuery('');
                                  }}
                                  style={{ borderInlineStartColor: style.color, borderInlineStartWidth: '3px', borderInlineStartStyle: 'solid' }}
                                >
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                                    {getLevelIcon(relativeLevel, style.color)}
                                    <div className="option-info" style={{ flex: 1 }}>
                                      <span dir="auto" className="option-name">
                                        {tenant.tenant_name}
                                        <span className="option-badge" style={{ background: style.bgColor, color: style.color }}>
                                          {relativeLevel === 0 ? 'ROOT' : `Level ${relativeLevel}`}
                                        </span>
                                      </span>
                                      <span dir="auto" className="option-meta">{hierarchyPath}</span>
                                    </div>
                                  </div>
                                  {createTenantForm.parent_tenant_id === tenant.tenant_id && (
                                    <svg className="option-check" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                      <polyline points="20 6 9 17 4 12" />
                                    </svg>
                                  )}
                                </button>
                              );
                            });
                          })()}
                        </div>
                      </div>
                    )}
                  </div>
                  <small style={{ color: 'var(--text-muted)', marginTop: '4px', display: 'block' }}>
                    {t('admin.selectWhereInTheHierarchy')}
                  </small>
                </div>

                <div className="form-group">
                  <label htmlFor="tenant-name">{t('admin.tenantNameLabel')} *</label>
                  <input dir="auto"
                    type="text"
                    id="tenant-name"
                    className="input"
                    required
                    placeholder="e.g., Engineering Department"
                    value={createTenantForm.tenant_name}
                    onChange={(e) => handleTenantNameChange(e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="tenant-code">{t('admin.tenantCodeLabel')} *</label>
                  <input dir="auto"
                    type="text"
                    id="tenant-code"
                    className="input"
                    required
                    placeholder="e.g., engineering-dept"
                    value={createTenantForm.tenant_code}
                    onChange={(e) =>
                      setCreateTenantForm({ ...createTenantForm, tenant_code: e.target.value })
                    }
                  />
                  <small style={{ color: 'var(--text-muted)' }}>
                    {t('admin.autoGeneratedUniqueIdentifierEdit')}
                  </small>
                </div>

              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setShowCreateTenantModal(false)}
                >
                  {t('vault.cancel')}
                </button>
                <button type="submit" className="btn-primary">
                  {t('groupAdmin.tenants.createTenant')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, type: null, source: null, id: '', name: '', loading: false })}
        onConfirm={deleteModal.source === 'org-tab' ? handleDeleteTenantUser : handleDeleteUser}
        title={t('admin.deleteUser')}
        message={`Are you sure you want to permanently delete "${deleteModal.name}"?\n\nThis will permanently delete all their conversations, messages, and data.\n\nThis action cannot be undone.`}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        variant="danger"
        loading={deleteModal.loading}
      />
    </div>
  );
}
